<template>
  <div class="main">
    <transition name="fade">
      <component-modal v-if="showModal" />
    </transition>
    <transition name="fade">
      <element-properties-modal v-if="getElementPropertiesModal !== null" />
    </transition>
    <vddl-list
      id="panzoom-parent"
      class="panzoom-parent"
      :list="tlist"
      :allowed-types="allowedTypes"
      :drop="onDropped"
      :style="styles"
    >
      <div v-if="showCrosshair" id="crosshair" />
      <board-tools
        v-if="getIsProjectLoaded"
        ref="boardTools"
        :panzoom="panzoom"
      />
      <guest-panel v-if="!getIsLoggedIn" />
      <div
        v-if="getIsProjectLoaded"
        id="panzoom"
        ref="panzoom"
        v-touch="onTouch"
        :class="[
          'panzoom',
          { guest: !canEdit },
          { showgrid: getGrid.show },
          { showSelections: getShowSelections },
          cursorModeClass,
        ]"
        @dblclick.self="onDblClickBoard($event)"
        @contextmenu.self.prevent="onContextMenu"
        @mousedown.self="onMouseDown($event)"
      >
        <div v-show="showRubberband" id="rubberband" ref="rubberband" />
        <template v-if="jspInitialized">
          <board-note
            v-for="note in notes"
            :id="note.id"
            :key="note.id"
            :virtual-selected="virtualSelection.includes(note.id)"
          />

          <board-branch
            v-for="branch in branches"
            :id="branch.id"
            ref="branches"
            :key="branch.id"
            :virtual-selected="virtualSelection.includes(branch.id)"
          />

          <board-jumper
            v-for="jumper in jumpers"
            :id="jumper.id"
            ref="jumpers"
            :key="jumper.id"
            :virtual-selected="virtualSelection.includes(jumper.id)"
          />

          <board-element
            v-for="element in elements"
            :id="element.id"
            :key="element.id"
            :virtual-selected="virtualSelection.includes(element.id)"
          />

          <board-connection
            v-for="connection in connections"
            :id="connection.id"
            :key="connection.id"
            :virtual-selected="virtualSelection.includes(connection.id)"
            :default-color="'#f3f3f3'"
          />

          <board-comment-pin
            v-for="comment in comments"
            :id="comment.id"
            :key="comment.id"
            :virtual-selected="virtualSelection.includes(comment.id)"
          />

          <board-dummy-element
            v-if="showDummyElement"
            id="dummy-element"
            ref="dummyElement"
            :styles="dummyElementPosition"
          />

          <user-cursor
            v-for="user in collaborators"
            :key="user.id"
            :color="user.color"
            :board-pos="user.cursor"
            :scale="userCursorScale"
            :transform="Number(transform.scale)"
            :name="user.fullName"
          />
        </template>
      </div>
      <floating-toolbar v-if="getIsProjectLoaded" />
    </vddl-list>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex';
import emitter from 'tiny-emitter/instance';
import {
  EVENT_DRAG_START,
  EVENT_DRAG_MOVE,
  EVENT_DRAG_STOP,
  EVENT_CONNECTION_CLICK,
  EVENT_CONNECTION_DBL_CLICK,
  EVENT_CONNECTION_CONTEXTMENU,
  newInstance,
} from '@jsplumb/browser-ui';
import jspSingleton from '@/lib/jspSingleton.js';
import {
  EVENT_CONNECTION,
  EVENT_CONNECTION_DETACHED,
  EVENT_CONNECTION_MOVED,
  INTERCEPT_BEFORE_DROP,
  INTERCEPT_BEFORE_DRAG,
} from '@jsplumb/core';
import _ from 'lodash';
import uuidv4 from 'uuid/v4';
import Panzoom from '@arcweave/panzoom';
import {
  DEVICE_MODE,
  PointerDeviceDetector,
} from '@/lib/pointerDeviceDetector.js';
import { useProtect } from '@/composables/useProtect.js';

import {
  SYS_PERMISSIONS,
  PANZOOM,
  EVENT,
  DRAG_TYPE,
  JSPLUMB,
} from '@/constants.js';
import BoardElement from './BoardElement.vue';
import BoardDummyElement from './BoardDummyElement.vue';
import BoardNote from './BoardNote.vue';
import BoardJumper from './BoardJumper.vue';
import BoardBranch from './BoardBranch/BoardBranch.vue';
import BoardConnection from './BoardConnection.vue';
import BoardCommentPin from './BoardComment/BoardCommentPin.vue';
import BoardTools from './BoardTools.vue';
import UserCursor from './UserCursor.vue';
import FloatingToolbar from '../floating-toolbar/FloatingToolbar.vue';
import ComponentModal from '../modals/ComponentModal/ComponentModal.vue';
import ElementPropertiesModal from '../modals/ElementPropertiesModal/ElementPropertiesModal.vue';

import GuestPanel from './GuestPanel.vue';

import misc from '../mixins/misc.js';
import Rect from '../../rect.js';
import updater from '../../vuex/project/updater.js';
import {
  updateURLQuery,
  viewportToCoords,
  loadTransformFromURLQuery,
  isDescendantOfClass,
} from '../../utils.js';
import { OPERATION, TYPE } from '../../vuex/application/clipboard.js';

export default {
  components: {
    BoardElement,
    BoardDummyElement,
    BoardNote,
    BoardJumper,
    BoardBranch,
    BoardConnection,
    BoardCommentPin,
    BoardTools,
    FloatingToolbar,
    ComponentModal,
    ElementPropertiesModal,
    UserCursor,
    GuestPanel,
  },

  mixins: [misc],

  setup() {
    const { hasPermission: canEdit } = useProtect({
      permissions: [SYS_PERMISSIONS.SYS_PROJECTS_EDIT],
      roles: ['editor'],
    });

    const { hasPermission: canComment } = useProtect({
      permissions: [SYS_PERMISSIONS.SYS_PROJECTS_COMMENT],
      roles: ['editor', 'viewer'],
    });

    return {
      canEdit,
      canComment,
    };
  },

  data() {
    return {
      tlist: [],
      allowedTypes: [
        DRAG_TYPE.ELEMENT_COMPONENT,
        DRAG_TYPE.ELEMENT_ASSET_AUDIO,
        DRAG_TYPE.COMPONENT,
        DRAG_TYPE.BOARD,
        DRAG_TYPE.BOARD_ELEMENT,
        DRAG_TYPE.NEW_ELEMENT,
        DRAG_TYPE.NEW_JUMPER,
        DRAG_TYPE.NEW_BRANCH,
        DRAG_TYPE.NEW_NOTE,
        DRAG_TYPE.NEW_COMMENT,
        DRAG_TYPE.SEARCH_ELEMENT,
        DRAG_TYPE.SEARCH_COMPONENT,
      ],
      jspInitialized: false,
      isDetaching: false,
      pointerStartPoint: null,
      rects: [], // An array containing the ids and the rectangles of all board items
      virtualSelection: [], // Which board items are temporarily selected in a rubberband
      movedConnectionProps: null, // temporarily stores a moved connection properties
      mousePos: { x: null, y: null }, // Mouse coordinates to notify other collaborators for cursor
      mousePosPrev: { x: null, y: null }, // Mouse coordinates of the previous interval
      exactMousePos: { x: null, y: null }, // Mouse coordinates used locally
      panZoomTransform: null, // temporary transform while panning or zooming
      showRubberband: false, // whether the rubberband is shown
      panningEnabled: true,
      isPanning: false,
      isDraggingConnection: false, // A connection endpoint is being dragged
      throttledMouseMove: null,
      activeHandlerElement: null,
      selectedNodesBoundingRect: null,
      draggingElementOriginalPosition: null,
      connectionCreated: false,
      showDummyElement: false,
      dummyElementPosition: {},
      debouncedOnThrottledZoom: null, // Used to save the callback in order to remove the event listener
      debouncedOnMouseWheel: null,
      onConnectionMouseUpHandler: null, // Used to save the callback of connectionMouseUp
      panzoom: null, // The panzoom object
      showCrosshair: false,
      isSpacePressed: false,
      cursorGrab: false, // Used for showing cursor class
      cursorGrabbing: false, // Used for showing cursor class
      pointerDetector: null,
      pointersDown: {}, // Currently down pointers
      pointerInteracting: false, // If the first pointer is interacting with a node (touch)
      draggingEnabled: true,
      setTransformTimeoutID: null,
    };
  },

  computed: {
    ...mapGetters({ bowser: 'getBowser' }),
    ...mapGetters('user/account', ['getIsLoggedIn', 'getId']),
    ...mapGetters('project/data/elements', {
      getElementRect: 'getRect',
      getElement: 'getNode',
      getElementBoundingRect: 'getBoundingRect',
    }),
    ...mapGetters('project/data/notes', {
      getNoteRect: 'getRect',
      getNote: 'getNode',
      getNoteBoundingRect: 'getBoundingRect',
    }),
    ...mapGetters('project/data/jumpers', {
      getJumper: 'getNode',
      getJumpers: 'getNodes',
    }),
    ...mapGetters('project/data/branches', {
      getBranch: 'getNode',
      getBranches: 'getNodes',
    }),
    ...mapGetters('project/data/comments', {
      getComment: 'getNode',
      getComments: 'getNodes',
    }),
    ...mapGetters('project/data/boards', [
      'getContainedNodes',
      'getRootContainer',
    ]),
    ...mapGetters('project/data/connections', ['getConnectableType']),
    ...mapGetters('project/data/connections', {
      getConnection: 'getNode',
    }),
    ...mapGetters('project/data/branches', ['getConditions']),
    ...mapGetters('project/config/boards', [
      // 'getSelections',
      'getTransform',
      'getOpen',
    ]),
    ...mapGetters('project/config/components', {
      getComponentOpen: 'getOpen',
    }),
    ...mapGetters('project/config/elements', {
      isElementSelectionEmpty: 'getIsSelectionEmpty',
      getElementSelections: 'getSelections',
    }),
    ...mapGetters('project/config/connections', ['getDefaultStyle']),
    ...mapGetters('project/config/connections', {
      isConnectionSelectionEmpty: 'getIsSelectionEmpty',
      getConnectionSelections: 'getSelections',
    }),
    ...mapGetters('project/config/notes', {
      isNoteSelectionEmpty: 'getIsSelectionEmpty',
    }),
    ...mapGetters('project/config/jumpers', {
      isJumperSelectionEmpty: 'getIsSelectionEmpty',
    }),
    ...mapGetters('project/config/branches', {
      isBranchSelectionEmpty: 'getIsSelectionEmpty',
    }),
    ...mapGetters('project/config/comments', {
      isCommentSelectionEmpty: 'getIsSelectionEmpty',
    }),
    ...mapGetters('project/config/comments', ['getShowResolved']),
    ...mapGetters('project/config', [
      'getGrid',
      'getSelections',
      'getCursorMode',
    ]),
    ...mapGetters('user/projects', {
      getOpenProject: 'getOpen',
    }),
    ...mapGetters('user/account', ['getId']),
    ...mapGetters('application/clipboard', ['getCanPaste', 'getClipboard']),
    ...mapGetters('application/appStates', [
      'getIsProjectLoaded',
      'getShowSelections',
    ]),
    ...mapGetters('application/channels', ['getCollaborators']),
    ...mapGetters('application/modals', ['getElementPropertiesModal']),
    ...mapGetters('application', [
      'getCursor',
      'getExactTransform',
      'getPointerDeviceAutoDetect',
      'getPointerDeviceMode',
    ]),

    /**
     * Return the css styles for positioning
     * @returns {Object}    The { left, top, width, height } styles
     */
    styles() {
      // CHECK THESE AT SOME POINT TO FIX RESIZE HANDLES TRANSFORM SIZE

      // const { scale } = this.exactTransform;
      // const newScale = 1 / scale;

      // $('.ui-resizable-handle').css('transform', `scale(${newScale})`);
      // $('.ui-resizable-w, .ui-resizable-e').css('height', `${100 / newScale}%`);
      // $('.ui-resizable-n, .ui-resizable-s').css('width', `${100 / newScale}%`);

      return {
        // '--transform-scale': `${scale}%`,
        // '--transform-default-scale': `${newScale}`,
      };
    },

    cursorModeClass() {
      if (this.getCursorMode === PANZOOM.CURSOR_MODE.GRAB) {
        return 'pan-mode';
      }
      return 'select-mode';
    },

    isCursorGrab() {
      if (
        !this.canEdit ||
        this.getCursorMode === PANZOOM.CURSOR_MODE.GRAB ||
        this.isSpacePressed
      ) {
        return true;
      }
      return false;
    },

    /**
     * Return the current board transform
     * @returns {Object}    The board transform
     */
    transform() {
      const transform = this.getTransform(this.getOpen);
      return {
        x: parseFloat(transform.x),
        y: parseFloat(transform.y),
        scale: parseFloat(transform.scale),
      };
    },

    /**
     * Return the current board transform
     * @returns {Object}    The board exact transform
     */
    exactTransform() {
      return this.getExactTransform;
    },

    /**
     * Return the exact transform scale in a string format
     @returns {string}    The exact transform scale
     */
    exactScale() {
      return `${parseInt(this.exactTransform.scale * 100, 10)}%`;
    },

    /**
     * Return the board elements
     * @returns {Array}    The elements
     */
    elements() {
      if (!jspSingleton.instance) return [];
      return this.getContainedNodes(this.getOpen, 'elements', 'elements');
    },

    /**
     * Return the board notes
     * @returns {Array}    The notes
     */
    notes() {
      if (!jspSingleton.instance) return [];
      return this.getContainedNodes(this.getOpen, 'notes', 'notes');
    },

    /**
     * Return the board jumpers
     * @returns {Array}    The jumpers
     */
    jumpers() {
      if (!jspSingleton.instance) return [];
      return this.getContainedNodes(this.getOpen, 'jumpers', 'jumpers');
    },

    /**
     * Return the board branches
     * @returns {Array}    The branches
     */
    branches() {
      if (!jspSingleton.instance) return [];
      return this.getContainedNodes(this.getOpen, 'branches', 'branches');
    },

    /**
     * Return the board connections
     * @returns {Array}    The connections
     */
    connections() {
      if (!jspSingleton.instance) return [];
      console.log(
        'GETTER FOR CONNECTIONS. Returning: ',
        this.getContainedNodes(this.getOpen, 'connections', 'connections')
      );
      return this.getContainedNodes(this.getOpen, 'connections', 'connections');
    },

    /**
     * Return the board comments (exlude resolved if they should be invisible)
     * @returns {Array}    The comments
     */
    comments() {
      // Comments are shown only to those that can comment
      if (!jspSingleton.instance || !this.canComment) return [];
      const nodes = this.getContainedNodes(
        this.getOpen,
        'comments',
        'comments'
      );
      return nodes.filter(
        comment => this.getShowResolved || (comment && !comment.resolved)
      );
    },

    /**
     * Return whether there is nothing selected on the open board
     * @returns {Boolean}  Whether there is at least one selection
     */
    isSelectionEmpty() {
      return (
        this.isElementSelectionEmpty &&
        this.isConnectionSelectionEmpty &&
        this.isNoteSelectionEmpty &&
        this.isJumperSelectionEmpty &&
        this.isBranchSelectionEmpty &&
        this.isCommentSelectionEmpty
      );
    },

    /**
     * Return the active collaborators (that have a cursor on the current board)
     * @returns {Array}
     */
    collaborators() {
      return this.getCollaborators.filter(user => {
        return user.board === this.getOpen && user.cursor;
      });
    },

    userCursorScale() {
      const transform = this.panZoomTransform || this.transform;
      return Number(transform.scale);
    },

    showModal() {
      return this.getIsProjectLoaded && this.getComponentOpen !== null;
    },
  },

  watch: {
    /**
     * Update pointerDetector autoDetect mode (from settings)
     */
    getPointerDeviceAutoDetect(newMode, oldMode) {
      if (this.pointerDetector) {
        this.pointerDetector.autoDetect = newMode;
      }
    },

    /**
     * Update pointerDetector mode (from settings)
     */
    getPointerDeviceMode(newMode, oldMode) {
      this.pointerDetector.setMode(newMode, true);
    },

    /**
     * Update the cursor of panzoom based on cursor mode
     */
    getCursorMode(newMode, oldMode) {
      const cursor = newMode === PANZOOM.CURSOR_MODE.GRAB ? 'grab' : 'default';
      this.panzoom.setOptions({ cursor });
    },

    /**
     * Watch open board change. Remove any toolbar, selections and update the transform.
     */
    getOpen(newId, oldId) {
      console.log('getOpen watcher is now called');
      emitter.emit(EVENT.PANZOOMTOTRANSFORM, this.transform);
      this.panZoomTransform = this.exactTransform;
      // jspSingleton.instance.setSuspendDrawing(true);

      // Remove any toolbar
      this.removeToolbar();

      this.$nextTick(() => {
        this.clearSelections();
        this.updateTransform();
        // jspSingleton.instance.setSuspendDrawing(false, true);
      });
      this.whisperBoard({ hash: this.getOpenProject, board: newId });
    },

    /**
     * Watch new project load. Remove selections, re-add listeners.
     */
    getIsProjectLoaded(newReady, oldready) {
      jspSingleton.instance.deleteEveryEndpoint();
      this.$nextTick(() => {
        this.clearSelections();
        this.addListeners();
      });
    },

    /**
     * Watch transform change.
     */
    transform(newValue, oldValue) {
      // Whenever the transform changes reposition the toolbar
      this.updateTransform();
      this.recalcAnchorRect();
    },

    /**
     * Watch grid snap property change to notify jsplumb for the new grid
     */
    'getGrid.snap': function s(newVal, oldVal) {
      const { snap, size } = this.getGrid;
      const grid = snap ? { h: size, w: size } : null;
      jspSingleton.instance?.setDragGrid(grid);
    },

    /**
     * Watch selection change.
     */
    getSelections(newValue, oldValue) {
      this.whisperSelections({ hash: this.getOpenProject });
    },

    /**
     * Watch role change. It is triggered when user role changes by the project owner
     */
    canEdit(newValue, oldValue) {
      // Refresh on canEdit change TO FIX (when jsplumb allows setting draggable on the fly)
      if (jspSingleton.instance) {
        jspSingleton.instance.elementsDraggable = newValue;
      }
      this.refreshAll();
    },

    /**
     * Watch open project change. It is triggered by open project change
     */
    getOpenProject(newValue, oldValue) {
      if (newValue) {
        this.$router.push({ path: `/project/${newValue}` });
      } else {
        this.createProject({ template: 'empty' }).then(
          response => {
            const { hash } = response.data;
            // this.$router.push({ path: `/project/${hash}` });
          },
          error => {
            console.error('Project failed to create. Error:', error);
          }
        );
      }
    },

    /**
     * Watch default connection style type or theme change, update jsplumb.
     */
    getDefaultStyle(newValue, oldValue) {
      const { type, theme } = newValue;
      const connector = JSPLUMB.connectors[type];
      if (theme) {
        // Set the theme color for new connectors
        connector.options.cssClass = `theme-${theme}`;
      }
      jspSingleton.instance.importDefaults({ connector });
    },
  },

  /**
   * Created lifecycle hook.
   */
  created() {
    // Make sure we store pan and zoom if user unfocuses/closes/refreshes window
    window.addEventListener('beforeunload', this.onWindowUnload);
  },

  /**
   * Mounted lifecycle hook.
   */
  mounted() {
    const { snap, size } = this.getGrid;
    const grid = snap ? { h: size, w: size } : null;
    const that = this;
    console.log(
      'jsplumb Initial options:',
      JSPLUMB.getInitialOptions(this.$refs.panzoom, grid)
    );
    // Initialize jsplumb and set up defaults
    jspSingleton.createInstance({
      ...JSPLUMB.getInitialOptions(this.$refs.panzoom, grid),
      dragOptions: {
        // containment: 'parentEnclosed',
        ...JSPLUMB.defaults.dragOptions,
        constrainFunction(pos, el, constrainRect, elSize) {
          const originalPosition = that.draggingElementOriginalPosition;
          let minX = 0;
          let maxX = constrainRect.w - elSize.w;
          let minY = 0;
          let maxY = constrainRect.h - elSize.h;
          if (that.selectedNodesBoundingRect) {
            minX =
              originalPosition.x - that.selectedNodesBoundingRect.leftValue;
            maxX =
              constrainRect.w -
              (that.selectedNodesBoundingRect.rightValue - originalPosition.x);
            minY = originalPosition.y - that.selectedNodesBoundingRect.topValue;
            maxY =
              constrainRect.h -
              (that.selectedNodesBoundingRect.bottomValue - originalPosition.y);
          }
          // eslint-disable-next-line prettier/prettier, no-nested-ternary
          const x = pos.x < minX ? minX + 1 : pos.x > maxX ? maxX - 1 : pos.x;
          // eslint-disable-next-line prettier/prettier, no-nested-ternary
          const y = pos.y < minY ? minY + 1 : pos.y > maxY ? maxY - 1 : pos.y;
          return { x, y };
        },
        cursor: 'pointer',
        zIndex: 5000,
        filter:
          '.comp, .comp *, .editor-input, .editor-input *, .audio-ref, .audio-ref *',
        grid,
      },
    });

    window.jspInstance = jspSingleton.instance;

    // Set up the default connection style
    const { type, theme } = this.getDefaultStyle;
    const connector = JSPLUMB.connectors[type];
    if (theme) {
      connector.options.cssClass = `theme-${theme}`;
    }
    jspSingleton.instance.importDefaults({ connector });

    // Set the source selector for elements
    jspSingleton.instance.addSourceSelector(
      '.plank',
      JSPLUMB.sourceSelectorOptions
    );

    // Set the source selector for connection handler left for elements
    jspSingleton.instance.addSourceSelector(
      '.plank .connection-handler.left',
      JSPLUMB.getSourceFaceSelectorOptions()
    );

    // Set the source selector for connection handler right for elements
    jspSingleton.instance.addSourceSelector(
      '.plank .connection-handler.right',
      JSPLUMB.getSourceFaceSelectorOptions()
    );

    // Set the source selector for connection handler top for elements
    jspSingleton.instance.addSourceSelector(
      '.plank .connection-handler.up',
      JSPLUMB.getSourceFaceSelectorOptions()
    );

    // Set the source selector for connection handler bottom for elements
    jspSingleton.instance.addSourceSelector(
      '.plank .connection-handler.down',
      JSPLUMB.getSourceFaceSelectorOptions()
    );

    // Set the target selector for elements
    jspSingleton.instance.addTargetSelector(
      '.plank',
      JSPLUMB.targetSelectorOptions
    );
    // jspSingleton.instance.addTargetSelector(
    //   '.handler',
    //   JSPLUMB.getCenterSelectorOptions()
    // );

    // Set the target selector for jumpers
    jspSingleton.instance.addTargetSelector(
      '.jumper',
      JSPLUMB.targetSelectorOptions
    );

    // Set the target selector for branches
    jspSingleton.instance.addTargetSelector(
      '.branch',
      JSPLUMB.branchTargetSelectorOptions
    );

    // Set the target selector for element left target
    jspSingleton.instance.addTargetSelector(
      '.connection-handler.left',
      JSPLUMB.getFaceSelectorOptions('left')
    );

    // Set the target selector for element right target
    jspSingleton.instance.addTargetSelector(
      '.connection-handler.right',
      JSPLUMB.getFaceSelectorOptions('right')
    );

    // Set the target selector for element top target
    jspSingleton.instance.addTargetSelector(
      '.connection-handler.up',
      JSPLUMB.getFaceSelectorOptions('top')
    );

    // Set the target selector for element bottom target
    jspSingleton.instance.addTargetSelector(
      '.connection-handler.down',
      JSPLUMB.getFaceSelectorOptions('bottom')
    );
    this.jspInitialized = true;
    this.refreshAll(true);
  },

  /**
   * beforeUnmount lifecycle hook.
   */
  beforeUnmount() {
    if (this.pointerDetector) this.pointerDetector.destroy();

    // Cancel throttle before destroying panzoom
    this.throttledMouseMove.cancel();
    this.removeListeners();
    this.panzoom.destroy();
  },

  methods: {
    ...mapActions('project/config/boards', ['setTransform']),
    ...mapActions('project/data/elements', {
      createElement: 'customCreate',
      updateElement: 'customUpdate',
      addComponent: 'addComponent',
    }),
    ...mapActions('project/data/notes', {
      createNote: 'customCreate',
      updateNote: 'customUpdate',
    }),
    ...mapActions('project/data/jumpers', {
      createJumper: 'customCreate',
      updateJumper: 'customUpdate',
    }),
    ...mapActions('project/data/branches', {
      createBranch: 'customCreate',
      updateBranch: 'customUpdate',
    }),
    ...mapActions('project/data/comments', {
      createComment: 'customCreate',
      updateComment: 'customUpdate',
    }),
    ...mapActions('project/data/connections', {
      createConnection: 'customCreate',
      updateConnection: 'customUpdate',
      deleteConnection: 'customDelete',
    }),
    ...mapActions('project/config/elements', {
      selectManyElements: 'selectMany',
      selectElement: 'select',
    }),
    ...mapActions('project/config/notes', {
      selectManyNotes: 'selectMany',
      selectNote: 'select',
    }),
    ...mapActions('project/config/jumpers', {
      selectManyJumpers: 'selectMany',
      selectJumper: 'select',
    }),
    ...mapActions('project/config/branches', {
      selectManyBranches: 'selectMany',
      selectBranch: 'select',
    }),
    ...mapActions('project/config/connections', {
      selectManyConnections: 'selectMany',
      deselectAllConnections: 'deselectAll',
    }),
    ...mapActions('project/config/comments', {
      selectManyComments: 'selectMany',
      selectComment: 'select',
    }),
    ...mapActions('project/config/boards', ['setOpen']),
    ...mapActions('project/config', [
      'deselectAll',
      'deleteSelections',
      'setCursorMode',
    ]),
    ...mapActions('application/contextToolbar', [
      'removeToolbar',
      'recalcAnchorRect',
    ]),
    ...mapActions('application/clipboard', [
      'cut',
      'copy',
      'paste',
      'shiftPaste',
    ]),
    ...mapActions('application/contextMenu', [
      'createItem',
      'setCalcPosition',
      'deleteMenu',
      'setOnDeleteMenu',
    ]),
    ...mapActions('application/modals', ['setBoardPropertiesModal']),
    ...mapActions('application/channels', [
      'whisperCursor',
      'whisperBoard',
      'whisperSelections',
    ]),
    ...mapActions('application', ['setCursor']),
    ...mapActions('user/projects', ['createProject']),
    ...mapMutations('application', [
      'setExactTransform',
      'setPanning',
      'setPointerDeviceMode',
    ]),

    /**
     * Refreshes all
     */
    refreshAll(mounted = false) {
      // Reset panzoom transform
      this.panZoomTransform = null;
      // Remove any listeners
      this.removeListeners();
      // Clear any panzoom
      if (this.panzoom) this.panzoom.destroy();
      if (this.pointerDetector) this.pointerDetector.destroy();

      this.pointerDetector = new PointerDeviceDetector({
        bowser: this.bowser,
        currentMode: DEVICE_MODE.MOUSE,
        parentElement: this.$refs.panzoom.parentElement,
        canvasElement: this.$refs.panzoom,
        onModeChange: (newMode, oldMode) => {
          console.log('Canvas Mode Changed from', oldMode, 'to', newMode);
          this.setPointerDeviceMode({ value: newMode });
        },
      });
      const { min, max, step } = PANZOOM.SCALE_LIMITS;
      // Register the pointer events before Panzoom registers it's own.
      this.$refs.panzoom.addEventListener(
        'pointerdown',
        this.onPointerDown,
        true
      );
      this.$refs.panzoom.addEventListener('pointerup', this.onPointerUp, true);
      // Reset panzoom
      this.panzoom = Panzoom(this.$refs.panzoom, {
        cursor: 'default',
        contain: 'outside',
        step,
        minScale: min,
        maxScale: max,
        duration: 300, // animation duration
        // disableZoom: true,
        setTransform: (elem, { x, y, scale, isSVG }, options) => {
          const mX = scale * x;
          const mY = scale * y;
          this.panzoom.setStyle(
            'transform',
            `matrix(${scale}, 0, 0, ${scale}, ${mX}, ${mY})`
          );
        },
        handleStartEvent: event => {
          if (this.panningEnabled) {
            this.panzoom.setOptions({ disablePan: false });
            event.preventDefault();
            event.stopPropagation();
          } else {
            this.panzoom.setOptions({ disablePan: true });
          }
        },
      });

      if (this.getIsProjectLoaded) {
        this.$nextTick(() => {
          let transformVals = this.transform;
          let board = this.getOpen;

          // Change board, scale and coords based on the URL query
          if (mounted) {
            const { board: queryBoard, transform } =
              loadTransformFromURLQuery();
            if (queryBoard) {
              board = queryBoard;
              this.setOpen({
                id: board,
                apply: true,
              });
            }

            const { scale, x, y } = transform;
            if (
              !Number.isNaN(parseFloat(scale)) &&
              !Number.isNaN(parseFloat(x)) &&
              !Number.isNaN(parseFloat(y))
            ) {
              transformVals = transform;
            }
          }
          setTimeout(() => {
            emitter.emit(EVENT.PANZOOMTOTRANSFORM, transformVals);
            this.setTransform({
              id: board,
              transform: transformVals,
              apply: true,
            });
            this.panZoomTransform = transformVals;
            this.setExactTransform({ transform: transformVals });
            updateURLQuery(this.$route.path, board, transformVals);
          });
          this.clearSelections();
          this.addListeners();
        });
      }
    },

    /**
     * Update the transform (requires panzoom initialization)
     */
    updateTransform() {
      // Do nothing if there is no transform
      if (this.transaform === null) return;
      updateURLQuery(this.$route.path, this.getOpen, this.transform);
      jspSingleton.instance?.setZoom(this.transform.scale);
    },

    /**
     * Add the event listeners for various events
     */
    addListeners() {
      emitter.on(EVENT.DELETE_SELECTIONS, this.removeSelections);
      emitter.on(EVENT.SELECT_ALL, this.onSelectAll);
      emitter.on(EVENT.COPY, this.onCopy);
      emitter.on(EVENT.CUT, this.onCut);
      emitter.on(EVENT.PASTE, this.onPaste);
      emitter.on(EVENT.SHIFT_PASTE, this.onShiftPaste);
      emitter.on(EVENT.ESC_BOARD, this.onEscEvent);

      // Jsplumb connection events
      if (this.canEdit) {
        jspSingleton.instance.bind(
          EVENT_CONNECTION_CLICK,
          this.onClickConnection
        );
        jspSingleton.instance.bind(
          EVENT_CONNECTION_CONTEXTMENU,
          this.onRightClickConnection
        );
        jspSingleton.instance.bind(
          EVENT_CONNECTION_DBL_CLICK,
          this.onDblClickConnection
        );
      }

      // Jsplumb drag element event listeners
      jspSingleton.instance.bind(EVENT_DRAG_START, this.onDragStart);
      jspSingleton.instance.bind(EVENT_DRAG_MOVE, this.onDragMove);
      jspSingleton.instance.bind(EVENT_DRAG_STOP, this.onDragStop);

      jspSingleton.instance.bind(EVENT_CONNECTION, this.onConnectionCreated);
      jspSingleton.instance.bind(INTERCEPT_BEFORE_DROP, this.onBeforeDrop);
      jspSingleton.instance.bind(INTERCEPT_BEFORE_DRAG, this.onBeforeDrag);
      jspSingleton.instance.bind(
        EVENT_CONNECTION_MOVED,
        this.onConnectionMoved
      );
      jspSingleton.instance.bind(
        EVENT_CONNECTION_DETACHED,
        this.onConnectionDetached
      );

      // Panzoom event handlers
      this.debouncedOnThrottledZoom = _.debounce(this.onThrottledZoom, 500);
      this.$refs.panzoom.addEventListener(
        'panzoomzoom',
        this.debouncedOnThrottledZoom
      );
      this.$refs.panzoom.addEventListener('panzoomzoom', this.onZoom);
      this.$refs.panzoom.addEventListener('panzoomstart', this.onPanStart);
      this.$refs.panzoom.addEventListener('panzoompan', this.onPan);
      this.$refs.panzoom.addEventListener('panzoomend', this.onPanEnd);
      this.$refs.panzoom.addEventListener('panzoomreset', this.onPanReset);
      this.$refs.panzoom.addEventListener('pointermove', this.onMouseMove);

      this.debouncedOnMouseWheel = _.debounce(this.onThrottledWheel, 500);
      this.$refs.panzoom.parentElement.addEventListener(
        'wheel',
        this.debouncedOnMouseWheel
      );
      this.$refs.panzoom.parentElement.addEventListener(
        'wheel',
        this.onMousewheelFocal
      );
      this.$refs.panzoom.addEventListener(
        'transitionend',
        this.onTransitionEnd
      );

      this.$refs.panzoom.parentElement.addEventListener(
        'scroll',
        this.onPanzoomParentScroll
      );

      this.throttledMouseMove = _.throttle(this.onThrottledMouseMove, 700);

      this.$refs.panzoom.addEventListener('mousemove', this.throttledMouseMove);
      this.$refs.panzoom.addEventListener(
        'mouseenter',
        this.onThrottledMouseMove
      );

      // Event listeners for space pan
      document.addEventListener('keydown', this.onKeyDown);
      document.addEventListener('keyup', this.onKeyUp);

      // Update transform
      this.$nextTick(() => {
        this.updateTransform();
      });

      jspSingleton.instance.repaintEverything();
    },

    /**
     * Remove the event listeners for various events
     */
    removeListeners() {
      if (this.canEdit) {
        // Remove all custom event listeners
        emitter.off(EVENT.DELETE_SELECTIONS);
        emitter.off(EVENT.SELECT_ALL);
        emitter.off(EVENT.COPY);
        emitter.off(EVENT.CUT);
        emitter.off(EVENT.PASTE);
        emitter.off(EVENT.SHIFT_PASTE);
        emitter.off(EVENT.ESC_BOARD);

        // Remove all jsplumb connection event listeners
        jspSingleton.instance.unbind(EVENT_CONNECTION_DBL_CLICK);
        jspSingleton.instance.unbind(EVENT_CONNECTION_CLICK);
        jspSingleton.instance.unbind(EVENT_CONNECTION_CONTEXTMENU);
        // jspSingleton.instance.unbind('connectionDrag');
        // jspSingleton.instance.unbind('connectionDragStop');
      }

      jspSingleton.instance.unbind(EVENT_CONNECTION);
      jspSingleton.instance.unbind(EVENT_CONNECTION_DETACHED);
      jspSingleton.instance.unbind(EVENT_CONNECTION_MOVED);

      jspSingleton.instance.unbind(INTERCEPT_BEFORE_DROP);
      jspSingleton.instance.unbind(INTERCEPT_BEFORE_DRAG);

      // Panzoom event handlers
      this.$refs.panzoom.removeEventListener(
        'panzoomzoom',
        this.debouncedOnThrottledZoom
      );
      this.$refs.panzoom.removeEventListener('panzoomzoom', this.onZoom);
      this.$refs.panzoom.removeEventListener('panzoomstart', this.onPanStart);
      this.$refs.panzoom.removeEventListener('panzoompan', this.onPan);
      this.$refs.panzoom.removeEventListener('panzoomend', this.onPanEnd);
      this.$refs.panzoom.removeEventListener('panzoomreset', this.onPanReset);
      this.$refs.panzoom.removeEventListener('pointermove', this.onMouseMove);
      this.$refs.panzoom.removeEventListener(
        'mousemove',
        this.throttledMouseMove
      );
      this.$refs.panzoom.removeEventListener(
        'mouseenter',
        this.onThrottledMouseMove
      );
      this.$refs.panzoom.removeEventListener(
        'transitionend',
        this.onTransitionEnd
      );
      this.$refs.panzoom.parentElement.removeEventListener(
        'scroll',
        this.onPanzoomParentScroll
      );
      if (this.panzoom) {
        this.$refs.panzoom.parentElement.removeEventListener(
          'wheel',
          this.onMousewheelFocal
        );
        this.$refs.panzoom.parentElement.removeEventListener(
          'wheel',
          this.debouncedOnMouseWheel
        );
      }
      document.removeEventListener('pointerdown', this.onPointerDown);
      document.removeEventListener('pointerup', this.onPointerUp);

      document.removeEventListener('keydown', this.onKeyDown);
      document.removeEventListener('keyup', this.onKeyUp);
    },

    /**
     * Sends a cursor movement to other users
     */
    sendCursor() {
      if (
        this.mousePosPrev.x !== this.mousePos.x ||
        this.mousePosPrev.y !== this.mousePos.y
      ) {
        const hash = this.getOpenProject;
        const pos = this.transformViewportToBoard(this.getCursor);
        this.whisperCursor({ hash, pos });
      }
      this.mousePosPrev.x = this.mousePos.x;
      this.mousePosPrev.y = this.mousePos.y;
    },

    /**
     * Transforms viewport coordinates to board coordinates
     */
    transformViewportToBoard(coords) {
      const vp = this.getViewPort();
      const transform = this.panZoomTransform || this.transform;
      if ($('.sidebar').css('position') === 'absolute') {
        // for mobile
        return {
          x: coords.x / transform.scale + vp.left,
          y: (coords.y - $('nav').height()) / transform.scale + vp.top,
        };
      }
      return {
        x: (coords.x - $('.sidebar').width()) / transform.scale + vp.left,
        y: (coords.y - $('nav').height()) / transform.scale + vp.top,
      };
    },

    /**
     * Transforms board to viewport coordinates
     */
    transformBoardToViewport(coords) {
      const vp = this.getViewPort();
      const transform = this.panZoomTransform || this.transform;
      const x = (coords.x - vp.left) * transform.scale;
      const y = (coords.y - vp.top) * transform.scale;
      return { x, y };
    },

    /**
     * Event listener - vddl drop event, something was dropped on the board.
     */
    async onDropped(event) {
      const coords = {
        x: event.event.clientX,
        y: event.event.clientY,
      };
      const position = this.transformViewportToBoard(coords);

      if (
        event.type === DRAG_TYPE.COMPONENT ||
        event.type === DRAG_TYPE.SEARCH_COMPONENT
      ) {
        const { name, id } = event.item;
        const title = `<span class="mention mention-component" data-id="${id}" data-type="component">${name}</span>`;
        const props = {
          title,
          components: [id],
        };
        const elementId = await this.createElement({
          boardId: this.getOpen,
          position,
          props: { title },
          apply: false,
        });
        this.addComponent({
          id: elementId,
          componentId: id,
          index: 0,
          apply: true,
        }).then(response => {
          this.setCursorMode({
            value: PANZOOM.CURSOR_MODE.SELECT,
            apply: true,
          });
        });
      } else if (
        event.type === DRAG_TYPE.BOARD_ELEMENT ||
        event.type === DRAG_TYPE.SEARCH_ELEMENT
      ) {
        const { id } = event.item;
        const props = {
          elementId: id,
        };
        this.createJumper({
          boardId: this.getOpen,
          position,
          props,
          apply: true,
        }).then(response => {
          window.getSelection().removeAllRanges();
          this.deselectAll({ apply: false });
          this.setCursorMode({
            value: PANZOOM.CURSOR_MODE.SELECT,
            apply: false,
          });
          this.selectJumper({ id: response, apply: true });
        });
      } else if (event.type === DRAG_TYPE.BOARD) {
        const { name, id } = event.item;
        const title = `<span class="mention mention-board" data-id="${id}" data-type="board">${name}</span>`;
        const props = {
          title,
        };
        this.createElement({
          boardId: this.getOpen,
          position,
          props,
          apply: true,
        }).then(response => {
          this.setCursorMode({
            value: PANZOOM.CURSOR_MODE.SELECT,
            apply: true,
          });
        });
      } else if (event.type === DRAG_TYPE.NEW_ELEMENT) {
        this.createElement({
          boardId: this.getOpen,
          position,
          props: {},
          apply: true,
        }).then(response => {
          window.getSelection().removeAllRanges();
          this.deselectAll({ apply: false });
          this.setCursorMode({
            value: PANZOOM.CURSOR_MODE.SELECT,
            apply: false,
          });
          this.selectElement({ id: response, apply: true });
        });
      } else if (event.type === DRAG_TYPE.NEW_JUMPER) {
        this.createJumper({
          boardId: this.getOpen,
          position,
          props: {},
          apply: true,
        }).then(response => {
          window.getSelection().removeAllRanges();
          this.deselectAll({ apply: false });
          this.setCursorMode({
            value: PANZOOM.CURSOR_MODE.SELECT,
            apply: false,
          });
          this.selectJumper({ id: response, apply: true });
        });
      } else if (event.type === DRAG_TYPE.NEW_BRANCH) {
        this.createBranch({
          boardId: this.getOpen,
          position,
          props: {},
          apply: true,
        }).then(response => {
          window.getSelection().removeAllRanges();
          this.deselectAll({ apply: false });
          this.setCursorMode({
            value: PANZOOM.CURSOR_MODE.SELECT,
            apply: false,
          });
          this.selectBranch({ id: response, apply: true });
        });
      } else if (event.type === DRAG_TYPE.NEW_NOTE) {
        this.createNote({
          boardId: this.getOpen,
          position,
          props: {},
          apply: true,
        }).then(response => {
          window.getSelection().removeAllRanges();
          this.deselectAll({ apply: false });
          this.setCursorMode({
            value: PANZOOM.CURSOR_MODE.SELECT,
            apply: false,
          });
          this.selectNote({ id: response, apply: true });
        });
      } else if (event.type === DRAG_TYPE.NEW_COMMENT) {
        const { x, y } = position;
        this.createComment({
          props: {
            userId: this.getId,
            position: { id: this.getOpen, type: 'boards', x, y },
            children: [],
            resolved: false,
          },
          apply: true,
        }).then(response => {
          window.getSelection().removeAllRanges();
          this.deselectAll({ apply: false });
          this.selectComment({ id: response, apply: true });
        });
      }
    },

    /**
     * Given the id of a connector returns if it is connected to a handler (face connector) the face
     * @param {HTMLElement} el The connector handler
     * @returns {String} The connector face: 'left', 'right', 'top', bottom' or null
     */
    getConnectorFace(el) {
      // Find the element this id corresponds to

      let face = null;
      if (el?.classList.contains('connection-handler')) {
        if (el?.classList.contains('left')) {
          face = 'left';
        } else if (el?.classList.contains('right')) {
          face = 'right';
        } else if (el?.classList.contains('up')) {
          face = 'top';
        } else if (el?.classList.contains('down')) {
          face = 'bottom';
        }
      }
      return face;
    },

    /**
     * Event listener - Jsplumb EVENT_CONNECTION. A connection has been created.
     * The event is also being called for connections that are loaded from the store.
     * @param {ConnectionEstablishedParams} params
     */
    onConnectionCreated(params, originalEvent) {
      // console.log('EVENT_CONNECTION-params:', params, 'or:', originalEvent);
      // Set the connection id to a uuid value
      params.connection.id = uuidv4();
      if (originalEvent !== undefined) {
        const { id } = params.connection;
        const { targetId, sourceId } = params.connection;
        const sourceType = this.getConnectableType(sourceId, 'source');
        const targetType = this.getConnectableType(targetId, 'target');

        const { endpoints } = params.connection;
        const sourceFace = endpoints[0].parameters.handler;
        const targetFace = endpoints[1].parameters.handler;

        console.log('data handler sourceFace:', sourceFace);
        console.log('data handler targetFace:', targetFace);

        // if (sourceFace || targetFace) {
        //   console.log('Deleting the connection here since it is using a face');
        //   // Delete any jsplumb connection to a face element - we'll create one to the main element
        //   jspSingleton.instance.deleteConnection(params.connection);
        // }

        // New connection was created
        const { type, theme } = this.getDefaultStyle;
        let props = {
          id,
          sourceid: sourceId,
          targetid: targetId,
          sourceType,
          targetType,
          label: this.movedConnectionProps?.label || null,
          type: this.movedConnectionProps?.type || type,
          theme: this.movedConnectionProps?.theme || theme,
        };
        // If the connection has a source face we apply it also
        // if (sourceFace) {
        //   props = { ...props, sourceFace };
        // }
        // If the connection has a target face we apply it also
        if (targetFace) {
          props = { ...props, targetFace };
        }
        // If the connection was moved we also apply its stored props
        let movedId = null;
        if (this.movedConnectionProps) {
          movedId = this.movedConnectionProps.id;
          if (sourceFace) {
            props = { ...props, sourceFace };
          }
          props = {
            ...this.movedConnectionProps,
            ...props,
          };
        }

        console.log('Creating new connection-props:', props);
        this.createConnection({
          boardId: this.getOpen,
          props,
          apply: false,
          movedId,
        });

        // Delete the object from the old connection on move (the order is important or else create may be confused)
        if (this.movedConnectionProps) {
          console.log('Deleting previous connection from store:', movedId);
          // Delete the connection references only if moving source endpoint or the connection is from element (outputs array)
          // If it is from condition it will overwrite the reference modified by createConnection
          const oldConnection = this.getConnection(movedId);
          const moveSource = oldConnection.sourceid !== props.sourceid;

          const deleteReferences = moveSource || sourceType === 'elements';
          this.deleteConnection({
            id: movedId,
            deleteReferences,
            apply: false,
          });
          this.movedConnectionProps = null;
        }

        updater.complete({ apply: true });
        this.connectionCreated = true;
      }
    },

    /**
     * Event listener - Jsplumb connectionDetached event.
     * A connection has been removed (a connection endpoint was left unattached).
     * @param {ConnectionDetachedParams}
     */
    onConnectionDetached(params, originalEvent) {
      // console.log('onConnectionDetached params:', params, originalEvent);
      // If not detached programmatically remove from the store
      if (originalEvent !== undefined) {
        console.log('Connection detached using the mouse');
        const { connection, sourceEndpoint, targetEndpoint } = params;
        // Clear any endpoint classes
        JSPLUMB.removeClass(sourceEndpoint, 'theme-', false);
        JSPLUMB.removeClass(targetEndpoint, 'theme-', false);
        JSPLUMB.removeClass(sourceEndpoint, 'collab-', false);
        JSPLUMB.removeClass(targetEndpoint, 'collab-', false);
        sourceEndpoint.removeClass('selected', false);
        targetEndpoint.removeClass('selected', false);
        // Delete the conection
        this.deleteConnection({ id: connection.id, apply: true });
      }
      this.isDetaching = false;
    },

    /**
     * Event listener for jsplumb EVENT_CONNECTION_MOVED event.
     * This is event will trigger an EVENT_CONNECTION afterwards.
     * @param {ConnectionMovedParams} params
     */
    onConnectionMoved(params) {
      console.log('EVENT_CONNECTION_MOVED:', params);
      const {
        connection: jspConnection,
        newSourceId,
        newTargetId,
        newEndpoint,
        originalEndpoint,
      } = params;
      const { id } = jspConnection;

      // Store the properties of the moved connection to apply them on Create
      const connection = this.getConnection(id);
      this.movedConnectionProps = {
        ...connection,
      };
      const isMovingSource = newSourceId === newEndpoint.elementId;
      const isMovingTarget = newTargetId === newEndpoint.elementId;
      // Clear any original endpoint classes
      JSPLUMB.removeClass(originalEndpoint, 'theme-', false);
      JSPLUMB.removeClass(originalEndpoint, 'collab-', false);
      originalEndpoint.removeClass('selected', false);

      // We are not storing the face for the moved endpoint since we'll override it
      if (isMovingSource && this.movedConnectionProps.sourceFace) {
        delete this.movedConnectionProps.sourceFace;
      }
      if (isMovingTarget && this.movedConnectionProps.targetFace) {
        delete this.movedConnectionProps.targetFace;
      }
    },

    /** Event handler for touchend OR mouseup when a connection is
     *  not created to show the context menu
     * @param {Object} params The jsPlumb connection parameters
     * @param {Event} event   The mouseup or touchend event
     */
    connectionOnMouseUp(params, event) {
      if (!this.connectionCreated && this.canEdit) {
        let clientX;
        let clientY;
        if (event.type === 'touchend') {
          clientX = event.changedTouches[0].clientX;
          clientY = event.changedTouches[0].clientY;
          /// Remove the other event listener
          document.removeEventListener(
            'mouseup',
            this.onConnectionMouseUpHandler
          );
        } else {
          // mouseup
          clientX = event.clientX;
          clientY = event.clientY;
          /// Remove the other event listener
          document.removeEventListener(
            'touchend',
            this.onConnectionMouseUpHandler
          );
        }

        const element = document.getElementById('panzoom');
        const rect = element.getBoundingClientRect();
        const { scale } = this.exactTransform;

        const boardPos = {
          x: (clientX - rect.left) / scale,
          y: (clientY - rect.top) / scale,
        };
        this.dummyElementPosition = {
          left: `${boardPos.x}px`,
          top: `${boardPos.y}px`,
          width: `1px`,
          height: `1px`,
        };
        this.showDummyElement = true;
        this.$nextTick(() => {
          const { type, theme } = this.getDefaultStyle;
          jspSingleton.createDummyConnection(
            JSPLUMB.connect(
              params.source,
              this.$refs.dummyElement.$el,
              type,
              theme,
              {
                type: JSPLUMB.defaultAnchor,
                options: { cssClass: `theme-${theme}` },
              },
              JSPLUMB.defaultAnchor
            )
          );
        });
        this.createConnectionContextMenu(
          boardPos,
          clientX,
          clientY,
          params.source
        );
      }
    },

    /**
     * Event handler for jsplumb's INTERCEPT_BEFORE_DRAG event.
     * This event is fired when a connection is started
     * @param {BeforeDragParams} params
     * @returns {boolean|undefined} If the intercepted operation should abort return false
     */
    onBeforeDrag(params) {
      console.log('INTERCEPT_BEFORE_DRAG params:', params);
      this.deselectAll({ apply: true });
      this.connectionCreated = false;
      this.onConnectionMouseUpHandler = this.connectionOnMouseUp.bind(
        this,
        params
      );
      document.addEventListener('touchend', this.onConnectionMouseUpHandler, {
        once: true,
      });
      document.addEventListener('mouseup', this.onConnectionMouseUpHandler, {
        once: true,
      });
    },

    /**
     * Create the context menu for creating elements from a connection drag
     * @param {Object}  boardPos  The new element board position
     * @param {Number}  x         The client x
     * @param {Number}  y         The client y
     * @param {Object}  source    The source element
     */
    async createConnectionContextMenu(boardPos, x, y, source) {
      const { type, theme } = this.getDefaultStyle;
      const props = {
        sourceid: source.id,
        sourceType: this.getConnectableType(source.id, 'source'),
        label: null,
        type,
        theme,
      };
      this.createItem({
        name: 'Create Element here',
        icon: null,
        commands: {
          doAction: () => {
            this.createElement({
              boardId: this.getOpen,
              position: boardPos,
              props: null,
              apply: false,
            }).then(response => {
              props.targetid = response;
              props.targetType = 'elements';
              this.createConnection({
                boardId: this.getOpen,
                props,
                apply: true,
              });
            });
            this.deleteMenu();
          },
          isActive: () => true,
          isEnabled: () => true,
        },
        submenu: null,
      });

      this.createItem({
        name: 'Create Jumper here',
        icon: null,
        commands: {
          doAction: () => {
            this.createJumper({
              boardId: this.getOpen,
              position: boardPos,
              props: null,
              apply: false,
            }).then(response => {
              props.targetid = response;
              props.targetType = 'jumpers';
              this.createConnection({
                boardId: this.getOpen,
                props,
                apply: true,
              });
            });
            this.deleteMenu();
          },
          isActive: () => true,
          isEnabled: () => true,
        },
        submenu: null,
      });

      this.createItem({
        name: 'Create Branch here',
        icon: null,
        commands: {
          doAction: () => {
            this.createBranch({
              boardId: this.getOpen,
              position: boardPos,
              props: null,
              apply: false,
            }).then(response => {
              props.targetid = response;
              props.targetType = 'branches';
              this.createConnection({
                boardId: this.getOpen,
                props,
                apply: true,
              });
            });
            this.deleteMenu();
          },
          isActive: () => true,
          isEnabled: () => true,
        },
        submenu: null,
      });

      this.createItem({
        name: 'Cancel',
        icon: null,
        commands: {
          doAction: () => {
            this.deleteMenu();
          },
          isActive: () => true,
          isEnabled: () => true,
        },
        submenu: null,
      });

      this.setOnDeleteMenu({
        onDeleteMenu: () => {
          jspSingleton.deleteDummyConnection();
          this.showDummyElement = false;
        },
      });

      this.setCalcPosition({
        calcPosition: () => {
          return { left: x, top: y };
        },
      });
    },

    /**
     * Event handler for jsplumb's INTERCEPT_BEFORE_DROP event.
     * This event is fired when a new or existing connection has been dropped to a valid target
     * @param {BeforeDropParams} params Various information passed in the event
     * @param {string} params.sourceId The id of the source element in the connection
     * @param {string} params.targetId The id of the target element in the connection
     * @param {string} params.scope The scope of the connection
     * @param {Connection} params.connection The actual Connection object.
     *     You can access the 'endpoints' array in a Connection to get the Endpoints
     *     involved in the Connection, but be aware that when a Connection is being dragged,
     *     one of these Endpoints will always be a transient Endpoint that exists only for the life of the drag.
     *     To get the Endpoint on which the Connection is being dropped, use the dropEndpoint member.
     * @param {Endpoint} params.endpoint The actual Endpoint on which the connection is being dropped.
     *     This may be null, because it will not be set if the Connection is being dropped on an element
     *     on which makeTarget has been called.
     *
     * @returns {boolean|undefined} If the intercepted operation should continue return true, otherwise false or nothing.
     */
    onBeforeDrop(params) {
      console.log('INTERCEPT_BEFORE_DROP jsplumb interceptor', params);
      const { sourceId, targetId, dropEndpoint, connection } = params;
      console.log('Before drop sourceId', sourceId);
      console.log('Before drop targetId:', targetId);

      // Determine the types of the two connectables
      const sourceType = this.getConnectableType(sourceId, 'source');
      const targetType = this.getConnectableType(targetId, 'target');

      console.log(
        'INTERCEPT_BEFORE_DROP source type - target type:',
        sourceType,
        targetType
      );

      // Cancel drop if the specific type is not configured for this type of drop
      // if (!sourceType) return false;

      // Cancel if drop on the same endpoint we drag
      if (dropEndpoint === connection.suspendedEndpoint) {
        console.log('--- [Redropping on same endpoint-cancelling]');
        return false;
      }

      // Cancel drop of a connection from a condition to its parent branch
      if (
        sourceType === 'conditions' &&
        targetType === 'branches' &&
        this.getConditions(targetId).includes(sourceId)
      ) {
        return false;
      }

      // Cancel drop on the same connectable board item
      return sourceId !== targetId;
    },

    /**
     * Event handler for keydown
     * @param {KeyboardEvent} event
     */
    onKeyDown(event) {
      if (event.key === ' ') {
        this.isSpacePressed = true;
      }
    },

    /**
     * Event handler for keyup
     * @param {KeyboardEvent} event
     */
    onKeyUp(event) {
      if (event.key === ' ') {
        this.isSpacePressed = false;
      }
    },

    /**
     * Event handler for mousedown event on a board. Change cursor to grab.
     */
    onMouseDown(event) {
      // $('#panzoom').css('cursor', 'grab');
    },

    /**
     * Event listener - Double-click on board, create an element.
     */
    onDblClickBoard(event) {
      // Prevent if user has shift,ctrl or meta pressed
      if (event.ctrlKey || event.shiftKey || event.metaKey) return;
      if (this.canEdit && 'offsetX' in event && 'offsetY' in event) {
        this.setCursorMode({ value: PANZOOM.CURSOR_MODE.SELECT, apply: true });
        const position = {
          x: event.offsetX,
          y: event.offsetY,
        };
        console.log('position', position);
        this.createElement({
          boardId: this.getOpen,
          position,
          props: null,
          apply: true,
        }).then(response => {
          window.getSelection().removeAllRanges();
          this.deselectAll({ apply: false });
          this.selectElement({ id: response, apply: true });
        });
      }
    },

    onMouseMove(event) {
      this.exactMousePos.x = event.x;
      this.exactMousePos.y = event.y;
      if (this.showRubberband) {
        const { x, y } = event;
        const coords = viewportToCoords({
          clientX: x,
          clientY: y,
          panzoomEl: this.$refs.panzoom,
          transform: {
            scale: this.panzoom.getScale(), // use panzoom just in case transform isn't updated
            ...this.panzoom.getPan(),
          },
        });

        let width = coords.x - this.pointerStartPoint.x;
        let height = coords.y - this.pointerStartPoint.y;

        const left =
          width > 0
            ? this.pointerStartPoint.x
            : this.pointerStartPoint.x + width;
        const top =
          height > 0
            ? this.pointerStartPoint.y
            : this.pointerStartPoint.y + height;
        width = Math.abs(width);
        height = Math.abs(height);

        // Update the rubberband with the new values
        this.$refs.rubberband.style.top = `${top}px`;
        this.$refs.rubberband.style.left = `${left}px`;
        this.$refs.rubberband.style.height = `${height}px`;
        this.$refs.rubberband.style.width = `${width}px`;

        this.$refs.rubberband.setAttribute('data-right', left + width);

        const rubRect = new Rect(left, left + width, top, top + height);
        rubRect.normalize();
        // Virtually select anything in the rubberband rectangle
        this.createVisualRubberbandRect(rubRect);
      }
    },

    /**
     * Event listener - Mouse move on the board, notify other collaborators
     */
    onThrottledMouseMove(event) {
      // Capture mouse coordinates
      this.mousePos.x = event.clientX;
      this.mousePos.y = event.clientY;
      this.setCursor({ cursor: { x: event.clientX, y: event.clientY } });
      this.sendCursor();
    },

    /**
     * Event listener - Panzoom zoom event.
     */
    onThrottledZoom(e) {
      // Let jsplumb know that the connection scale has changed
      console.log('On throttled zoom called. IsPanning: ', this.isPanning);
      if (!this.isPanning) {
        console.log(e);
        const { scale, x, y, originalEvent } = e.detail;
        this.setTransform({
          id: this.getOpen,
          transform: { scale, x, y },
          apply: false,
        });
        let save = true;
        if (originalEvent?.type === 'ChannelEvent') {
          // If from channel, we do not want to update the DB
          save = false;
        }
        updater.complete({ apply: true, save });
      }
    },

    onZoom(e) {
      const { scale, x, y } = e.detail;
      jspSingleton.instance.setZoom(scale);
      this.panZoomTransform = {
        scale,
        x,
        y,
      };

      this.setExactTransform({ transform: this.panZoomTransform });
    },

    /**
     * Event listener - Panzoom pan event handler
     */
    onPan(e) {
      if (!this.panningEnabled) return;
      // When we are creating a rubberband
      const { x, y } = e.detail;
      if (!this.isPanning) {
        console.log('Panning Started');
        this.isPanning = true;
        this.setPanning({ value: true });
      }

      const transform = this.panZoomTransform || this.transform;
      this.panZoomTransform = { scale: transform.scale, x, y };
    },

    /**
     * Event handler - Panzoom Parent scroll
     * Disable scrolling from caret going out of parent viewport
     */
    onPanzoomParentScroll(e) {
      if (e.target.scrollTop !== 0 || e.target.scrollLeft !== 0) {
        const scale = this.panzoom.getScale();
        const result = this.panzoom.pan(
          -e.target.scrollLeft / scale,
          -e.target.scrollTop / scale,
          {
            relative: true,
            animate: false,
          }
        );
        this.setTransform({
          id: this.getOpen,
          transform: {
            scale: result.scale,
            x: result.x,
            y: result.y,
          },
          apply: true,
        });
        e.target.scrollTo(0, 0);
      }
    },

    /**
     * Event handler for pointer down in panzoom element.
     * Should run before Panzoom's inner pointer down event.
     * @param {PointerEvent} event
     */
    onPointerDown(event) {
      // Starting location
      this.pointerStartPoint = {
        x: event.offsetX,
        y: event.offsetY,
        clientX: event.clientX,
        clientY: event.clientY,
      };
      this.pointersDown[event.pointerId] = event;
      const pointersLen = Object.keys(this.pointersDown).length;

      console.log('--- onPointerDown:', event, this.pointersDown);
      this.isDraggingConnection = false;

      this.panningEnabled = false;
      this.draggingEnabled = true;

      if (this.canEdit) {
        if (event.pointerType === 'touch') {
          // If touch device
          if (pointersLen === 1) {
            if (event.target === this.$refs.panzoom) {
              this.cursorGrabbing = true;
              this.panningEnabled = true;
            } else {
              this.pointerInteracting = true;
              this.panningEnabled = false;
              this.panzoom.setOptions({ disablePan: true });
            }
          } else {
            // eslint-disable-next-line no-lonely-if
            if (this.pointerInteracting) {
              this.panningEnabled = false;
              this.panzoom.setOptions({ disablePan: true, disableZoom: true });
            } else {
              this.draggingEnabled = false;
              this.panningEnabled = true;
              this.panzoom.setOptions({
                disablePan: false,
                disableZoom: false,
              });
            }
          }
        } else if (this.getCursorMode === PANZOOM.CURSOR_MODE.GRAB) {
          if (event.button !== 2) {
            // Not right click
            this.cursorGrabbing = true;
            this.panningEnabled = true;
          }
        } else {
          // Cursor Mode === Select
          // eslint-disable-next-line no-lonely-if
          if (event.button === 1 || this.isSpacePressed) {
            // If middle click or space is pressed
            this.cursorGrabbing = true;
            this.panningEnabled = true;
          } else if (event.button === 0) {
            this.panningEnabled = false;
            this.panzoom.setOptions({ disablePan: true });
            if (event.target === this.$refs.panzoom) {
              this.startRubberband(event);
            } else {
              // Target is a node or content
              if (event.target.classList.contains('connection-handler')) {
                event.target.classList.add('active-handler');
                this.activeHandlerElement = event.target;
                this.isDraggingConnection = true;
                if (this.showDummyElement) {
                  this.showDummyElement = false;
                }
              } else if (
                event.target.classList.contains('condition-source') ||
                event.target.parentElement.classList.contains(
                  'condition-source'
                )
              ) {
                this.isDraggingConnection = true;
                if (this.showDummyElement) {
                  this.showDummyElement = false;
                }
              }
              this.deleteMenu();
            }
          }
        }
      } else {
        // Viewer or Guest
        // eslint-disable-next-line no-lonely-if
        if (
          this.canComment &&
          isDescendantOfClass(event.target, 'comment', this.$refs.panzoom)
        ) {
          // Viewer
          console.log('IS COMMENT');
          this.panningEnabled = false;
          this.panzoom.setOptions({ disablePan: true });
        } else {
          // Guest
          this.cursorGrabbing = true;
          this.panningEnabled = true;
          this.panzoom.setOptions({ disablePan: false, disableZoom: false });
        }
      }
    },

    /**
     * Event handler for pointer up in panzoom element.
     * Should run before Panzoom's inner pointer up event.
     * @param {PointerEvent} event
     */
    onPointerUp(event) {
      this.cursorGrabbing = false;
      delete this.pointersDown[event.pointerId];
      const pointersLen = Object.keys(this.pointersDown).length;
      if (pointersLen === 0) {
        this.draggingEnabled = true;
        this.pointerInteracting = false;
        this.panzoom.setOptions({ disablePan: false, disableZoom: false });
      } else if (pointersLen === 1) {
        this.draggingEnabled = true;
      }

      const hasMoved =
        event.clientX !== this.pointerStartPoint.clientX ||
        event.clientY !== this.pointerStartPoint.clientY;

      // Clear any active handler
      if (this.activeHandlerElement) {
        this.activeHandlerElement.classList.remove('active-handler');
        this.activeHanlderElement = null;
      }

      if (this.showRubberband) {
        this.endRubberband(event);
      }
      if (
        !this.isPanning &&
        !hasMoved &&
        (event.target === this.$refs.rubberband ||
          event.target === this.$refs.panzoom)
      ) {
        console.log('Clearing selections');
        this.clearSelections();
      }
    },

    /**
     * Initialize the rubberband
     */
    startRubberband(event) {
      if (!event.shiftKey) {
        this.deselectAll({ apply: true });
      }
      // Initial rubberband styling
      this.$refs.rubberband.style.top = `${this.pointerStartPoint.y}px`;
      this.$refs.rubberband.style.left = `${this.pointerStartPoint.x}px`;
      this.$refs.rubberband.style.height = '1px';
      this.$refs.rubberband.style.width = '1px';

      // Show rubberband
      this.showRubberband = true;
      document.body.classList.add('selecting');

      // Initialize node rects
      this.rects = [];
      this.elements.forEach(element => {
        this.rects.push({
          id: element.id,
          ...this.getElementRect(element).toObject(),
        });
      });
      this.notes.forEach(note => {
        this.rects.push({
          id: note.id,
          ...this.getNoteRect(note).toObject(),
        });
      });
      Array.from(document.getElementsByClassName('comment')).forEach(
        comment => {
          // Due to scaling we need to de-scale
          const { id, offsetLeft, offsetTop, offsetWidth, offsetHeight } =
            comment;
          // offsetLeft, offsetTop is at top left at 100% scale
          const { scale } = this.transform;
          // Find the middle at 100%
          let left = offsetLeft + offsetWidth / 2;
          let top = offsetTop + offsetHeight / 2;
          // Find top left with scale this time
          left -= offsetWidth / (2 * scale);
          top -= offsetHeight / (2 * scale);

          const right = left + offsetWidth / scale;
          const bottom = top + offsetHeight / scale;
          this.rects.push({ id, left, right, top, bottom });
        }
      );
      if (this.$refs.jumpers) {
        this.$refs.jumpers.forEach(jumper => {
          this.rects.push({
            id: jumper.id,
            left: jumper.$el.offsetLeft,
            right: jumper.$el.offsetLeft + jumper.$el.offsetWidth,
            top: jumper.$el.offsetTop,
            bottom: jumper.$el.offsetTop + jumper.$el.offsetHeight,
          });
        });
      }
      if (this.$refs.branches) {
        this.$refs.branches.forEach(branch => {
          this.rects.push({
            id: branch.id,
            left: branch.$el.offsetLeft,
            right: branch.$el.offsetLeft + branch.$el.offsetWidth,
            top: branch.$el.offsetTop,
            bottom: branch.$el.offsetTop + branch.$el.offsetHeight,
          });
        });
      }
      jspSingleton.instance.select().each(jspConnection => {
        // Use the start and end endpoints of this connection
        const epStart = jspConnection.endpoints[0].endpoint;
        const epEnd = jspConnection.endpoints[1].endpoint;
        const r = new Rect(
          epStart.x + epStart.w / 2,
          epEnd.x + epEnd.w / 2,
          epStart.y + epStart.h / 2,
          epEnd.y + epEnd.h / 2
        );
        this.rects.push({ id: jspConnection.id, ...r.toObject() });
      });
    },

    /**
     * End the rubberband showing
     */
    endRubberband() {
      // Compute the rubberband rectangle
      document.body.classList.remove('selecting');
      // Inform about the rubberband rectangle
      this.selectVisualRubberbandRect();
      this.showRubberband = false;
      this.virtualSelection = [];
    },

    /**
     * Event listener - Panzoom pan start event.
     */
    onPanStart(e) {
      if (this.panningEnabled && !this.isDraggingConnection) {
        console.log('OnPanStart e:', e);
        this.removeAllRanges();
        if (typeof this.setTransformTimeoutID === 'number') {
          clearTimeout(this.setTransformTimeoutID);
          this.setTransformTimeoutID = null;
        }
      }
    },

    /**
     * Event listener - Panzoom pan end event from move or pinch
     */
    onPanEnd(e) {
      const { x, y, scale } = e.detail;

      const changed =
        x !== this.transform.x ||
        y !== this.transform.y ||
        scale !== this.transform.scale;

      console.log(
        '--- onPanEnd changed:',
        changed,
        'is panning:',
        this.isPanning,
        'is dragging connection:',
        this.isDraggingConnection,
        'show rubberband:',
        this.showRubberband
      );

      this.panzoom.setOptions({ disablePan: false });
      if (this.isDraggingConnection) return;
      // Do nothing if rubberband is inactive
      if (!this.canEdit || !this.showRubberband) {
        console.log(
          'Is panning:',
          this.isPanning,
          'changed:',
          changed,
          'draggingConnection',
          this.isDraggingConnection
        );
        // Normal pan ended
        // $('body').removeClass('panning');
        // If actual pan apply the transform else (simple click) clear selections
        if (this.isPanning && changed) {
          this.setTransformTimeoutID = setTimeout(() => {
            console.log('Calculating and setting transform for panning');
            const transform = { scale, x, y };
            this.setTransform({ id: this.getOpen, transform, apply: true });
            this.setExactTransform({ transform });
          }, 500);
        }
      }
      this.isPanning = false;
      this.setPanning({ value: false });
      this.panZoomTransform = null;
    },

    onPanReset(event) {
      console.log('--- onPanReset', event);
      this.setTransform({
        id: this.getOpen,
        transform: {
          scale: event.detail.scale,
          x: event.detail.x,
          y: event.detail.y,
        },
        apply: true,
      });
    },
    /**
     * Event handler for transtionend event on panzoom (a css transition has finished).
     */
    onTransitionEnd(event) {
      // Do nothing if triggered by a child animation, only panzoom itself
      if (event.target === this.$refs.panzoom) {
        const scale = this.panzoom.getScale();
        const { x, y } = this.panzoom.getPan();
        console.log('On transition end x: ', x, 'y: ', y);
        this.setTransform({
          id: this.getOpen,
          transform: {
            scale,
            x,
            y,
          },
          apply: true,
        }).then(response => {
          // Notify jsplumb on the new scale
          this.isPanning = false;
          this.setPanning({ value: false });
          jspSingleton.instance.setZoom(this.transform.scale);
        });
      }
    },

    onThrottledWheel(e) {
      const scale = this.panzoom.getScale();
      const { x, y } = this.panzoom.getPan();
      this.setTransform({
        id: this.getOpen,
        transform: { scale, x, y },
        apply: true,
      });
      this.isPanning = false;
      this.setPanning({ value: false });
    },

    /**
     * Event listener - User uses mousewheel to zoom.
     */
    onMousewheelFocal(e) {
      // Do nothing if left mouse pressed to avoid simultaneous pan & zoom conflicts
      if (
        e.buttons !== 4 && // Panning w/ middle mouse
        !(e.buttons === 1 && this.isSpacePressed) && // Panning with space & left mouse
        !(this.getCursorMode === PANZOOM.CURSOR_MODE.GRAB && e.buttons === 1) // Panning w/ left mouse in grab mode
      ) {
        e.preventDefault();
        if (e.ctrlKey || e.metaKey) {
          if (this.pointerDetector.isPinchZoom(e)) {
            this.touchpadZoom(e, {
              animate: false,
              focal: e,
            });
          } else {
            this.wheelZoom(e, {
              animate: false,
              focal: e,
            });
          }
        } else if (this.getPointerDeviceMode === DEVICE_MODE.MOUSE) {
          this.wheelZoom(e, {
            animate: false,
            focal: e,
          });
        } else {
          let x = -e.deltaX;
          let y = -e.deltaY;
          if (e.shiftKey && !this.bowser.mac) {
            x = -e.deltaY;
            y = -e.deltaX;
          }
          this.panzoom.pan(x, y, { relative: true });
        }
      }
    },

    wheelZoom(event, opts) {
      const scale = this.panzoom.getScale();
      const de = (() => {
        const [majorVersion] = (
          this.bowser.parser.parsedResult.browser.version || ''
        ).split('.');
        if (this.bowser.firefox && this.bowser.windows) {
          if (Number(majorVersion || 1e3) <= 87) {
            return 40;
          }
          return 2.35;
        }
        if (this.bowser.firefox && Number(majorVersion || 1e3) <= 87) {
          return 40;
        }
        return 7.5;
      })();
      let delta;

      if (event.wheelDelta) {
        delta = event.wheelDelta;
      } else if (event.deltaY) {
        delta = -event.deltaY * de;
      } else {
        delta = -event.deltaX * de;
      }
      let transform = 1 + 0.001 * Math.abs(delta);
      if (delta < 0) {
        transform = 1 / transform;
      }
      const toScale = scale * transform;

      return this.panzoom.zoomToPoint(toScale, event, opts, event);
    },

    touchpadZoom(event, opts) {
      const scale = this.panzoom.getScale();

      const delta =
        event.deltaY === 0 && event.deltaX ? event.deltaX : event.deltaY;

      let toScale = scale * Math.exp(-delta / 100);

      toScale = Math.min(
        Math.max(toScale, PANZOOM.SCALE_LIMITS.min),
        PANZOOM.SCALE_LIMITS.max
      );
      return this.panzoom.zoomToPoint(toScale, event, opts, event);
    },

    /**
     * The rubberband rectangle is ready and everything is to be selected
     */
    selectVisualRubberbandRect() {
      console.log('Selecting virtual selection:', this.virtualSelection);
      const elementIds = [];
      const noteIds = [];
      const jumperIds = [];
      const branchIds = [];
      const connectionIds = [];
      const commentIds = [];
      this.virtualSelection.forEach(nodeId => {
        if (this.elements.find(node => node.id === nodeId)) {
          elementIds.push(nodeId);
        } else if (this.notes.find(node => node.id === nodeId)) {
          noteIds.push(nodeId);
        } else if (this.jumpers.find(node => node.id === nodeId)) {
          jumperIds.push(nodeId);
        } else if (this.branches.find(node => node.id === nodeId)) {
          branchIds.push(nodeId);
        } else if (this.connections.find(node => node.id === nodeId)) {
          connectionIds.push(nodeId);
        } else if (this.comments.find(node => node.id === nodeId)) {
          commentIds.push(nodeId);
        }
      });
      if (
        elementIds ||
        noteIds ||
        jumperIds ||
        branchIds ||
        connectionIds ||
        commentIds
      ) {
        this.selectManyElements({ ids: elementIds, apply: false });
        this.selectManyNotes({ ids: noteIds, apply: false });
        this.selectManyJumpers({ ids: jumperIds, apply: false });
        this.selectManyBranches({ ids: branchIds, apply: false });
        this.selectManyConnections({ ids: connectionIds, apply: false });
        this.selectManyComments({ ids: commentIds, apply: false });
        updater.complete({ apply: true });
      }
    },

    /**
     * Create the given rubberband rectangle.
     */
    createVisualRubberbandRect(rect) {
      this.virtualSelection = [];

      this.rects.forEach(node => {
        const nodeRect = new Rect(node.left, node.right, node.top, node.bottom);
        if (
          Rect.intersectRect(rect, nodeRect) ||
          Rect.containsRect(rect, nodeRect)
        ) {
          this.virtualSelection.push(node.id);
        }
      });
    },

    /**
     * Event listener - select all custom event.
     */
    onSelectAll() {
      if (this.canEdit) {
        // Select all elements
        const elementIds = [];
        this.elements.forEach(element => elementIds.push(element.id));
        // Select all notes
        const noteIds = [];
        this.notes.forEach(note => noteIds.push(note.id));
        // Select all jumpers
        const jumperIds = [];
        this.jumpers.forEach(jumper => jumperIds.push(jumper.id));
        // Select all branches
        const branchIds = [];
        this.branches.forEach(branch => branchIds.push(branch.id));
        // Select all connections
        const connectionIds = [];
        jspSingleton.instance
          .select()
          .each(jspConnection => connectionIds.push(jspConnection.id));
        // Select all comments
        const commentIds = [];
        this.comments.forEach(comment => commentIds.push(comment.id));

        if (elementIds || noteIds || jumperIds || branchIds || connectionIds) {
          this.selectManyElements({ ids: elementIds, apply: false });
          this.selectManyNotes({ ids: noteIds, apply: false });
          this.selectManyJumpers({ ids: jumperIds, apply: false });
          this.selectManyBranches({ ids: branchIds, apply: false });
          this.selectManyConnections({ ids: connectionIds, apply: false });
          this.selectManyComments({ ids: commentIds, apply: true });
        }
      }
    },

    /**
     * Click connection event handler
     */
    onClickConnection(jspConnection, event) {
      if (this.getCursorMode !== PANZOOM.CURSOR_MODE.GRAB) {
        emitter.emit(EVENT.CLICK_CONNECTION, jspConnection.id, event);
      }
    },

    /**
     * Click endpoint event handler
     */
    onClickEndpoint(endpoint, originalEvent) {
      if (this.getCursorMode !== PANZOOM.CURSOR_MODE.GRAB) {
        emitter.emit(EVENT.CLICK_ENDPOINT, endpoint, originalEvent);
      }
    },

    /**
     * Click endpoint event handler
     */
    onMousedownEndpoint(endpoint, originalEvent) {
      console.log('MOUSEDOWN:', endpoint);
      emitter.emit(EVENT.CLICK_ENDPOINT, endpoint, originalEvent);
    },

    /**
     * Right Click connection event handler
     */
    onRightClickConnection(jspConnection, event) {
      event.preventDefault();
      if (this.getCursorMode !== PANZOOM.CURSOR_MODE.SELECT) {
        this.setCursorMode({ value: PANZOOM.CURSOR_MODE.SELECT, apply: true });
      }
      emitter.emit(EVENT.RIGHT_CLICK_CONNECTION, jspConnection.id, event);
    },

    /**
     * Double-click connection event handler
     */
    onDblClickConnection(jspConnection, event) {
      console.log('Double click a connection');
      if (this.getCursorMode !== PANZOOM.CURSOR_MODE.SELECT) {
        this.setCursorMode({ value: PANZOOM.CURSOR_MODE.SELECT, apply: true });
      }
      emitter.emit(EVENT.DBL_CLICK_CONNECTION, jspConnection.id, event);
    },

    getSelectionBoundingRect() {
      const selections = this.getSelections;
      const elementsRect = this.getElementBoundingRect(
        selections.elements,
        true
      );
      const notesRect = this.getNoteBoundingRect(selections.notes, true);
      const jumpersRect = Rect.boundingRect(
        this.getJumpers(selections.jumpers).map(
          jumper => new Rect(jumper.x, jumper.x + 200, jumper.y, jumper.y + 81)
        )
      );
      const branchesRect = Rect.boundingRect(
        this.getBranches(selections.branches).map(
          branch => new Rect(branch.x, branch.x + 260, branch.y, branch.y + 110)
        )
      );
      const commentsRect = Rect.boundingRect(
        this.getComments(selections.comments).map(
          comment =>
            new Rect(
              comment.position.x,
              comment.position.x + 22,
              comment.position.y,
              comment.position.y + 20
            )
        )
      );
      const rects = [];
      if (elementsRect) {
        rects.push(elementsRect);
      }
      if (notesRect) {
        rects.push(notesRect);
      }
      if (jumpersRect) {
        rects.push(jumpersRect);
      }
      if (branchesRect) {
        rects.push(branchesRect);
      }
      if (commentsRect) {
        rects.push(commentsRect);
      }
      if (rects.length === 0) {
        return null;
      }
      return Rect.boundingRect(rects);
    },

    /**
     * Event listener for jsplumb draggable start event.
     * @param {DragStartPayload} params
     */
    onDragStart(params) {
      console.log('onDragStart params:', params);
      if (!this.draggingEnabled) {
        return false;
      }
      this.selectedNodesBoundingRect = this.getSelectionBoundingRect();
      this.draggingElementOriginalPosition = params.originalPosition;
      const element = params.el;
      const isSelected = element.classList.contains('jtk-drag-selected');
      if (!isSelected) {
        this.deselectAll({ apply: true });
        jspSingleton.instance?.clearDragSelection();
      }
      return true;
    },

    /**
     * A jsplumb draggable element has stoped dragging
     * @param {DragStopPayload} params
     */
    onDragStop(params) {
      console.log('onDragStop params:', params);
      const { elements } = params;
      // Change each element's position to the new one in multiselection
      elements.forEach(element => {
        const { id } = element.el;
        const isElement = this.getElement(id) !== null;
        const isJumper = this.getJumper(id) !== null;
        const isBranch = this.getBranch(id) !== null;
        const isNote = this.getNote(id) !== null;
        const isComment = this.getComment(id) !== null;
        const item = _.cloneDeep(
          this.getElement(id) ||
            this.getJumper(id) ||
            this.getBranch(id) ||
            this.getNote(id) ||
            this.getComment(id)
        );

        const { x, y } = element.pos;
        if (item.x !== x || item.y !== y) {
          // only if there is actual change
          if (isElement) {
            this.updateElement({
              id,
              props: { x, y },
              apply: false,
            });
          } else if (isJumper) {
            this.updateJumper({
              id,
              props: { x, y },
              apply: false,
            });
          } else if (isBranch) {
            this.updateBranch({
              id,
              props: { x, y },
              apply: false,
            });
          } else if (isNote) {
            this.updateNote({
              id,
              props: { x, y },
              apply: false,
            });
          } else if (isComment) {
            const comment = this.getComment(id);
            const newPosition = {
              ...comment.position,
              x,
              y,
            };
            this.updateComment({
              id,
              props: { position: newPosition },
              apply: false,
            });
          }
        }
      });
      updater.complete({ apply: true });
    },

    /**
     * A jsplumb draggable element is currently being dragged
     * @param {DragMovePayload}
     */
    onDragMove(params) {
      this.recalcAnchorRect();
    },

    /**
     * Clear all element, note and connection selections
     */
    clearSelections() {
      this.deselectAll({ apply: true });
    },

    /**
     * Delete the element, note and connection selections
     */
    removeSelections() {
      console.log('Removing selections');

      // Find all connection selections
      const connections = this.getConnectionSelections;
      console.log('connections', connections);

      this.deleteSelections({ apply: true });
    },

    /**
     * Returns a rect with the board coordinates of the view port
     * @return {Rect} The viewport rectangle
     */
    getViewPort() {
      // Find the position of the panzoom on the panzoom parent
      const panzoomRect = this.$refs.panzoom.getBoundingClientRect();
      const parentRect =
        this.$refs.panzoom.parentElement.getBoundingClientRect();
      const pzleft = -(panzoomRect.left - parentRect.left);
      const pztop = -(panzoomRect.top - parentRect.top);
      const pzright = pzleft + parentRect.width;
      const pzbottom = pztop + parentRect.height;
      const transform = this.panZoomTransform || this.transform;
      // Adjust the viewport scale
      const viewport = new Rect(
        pzleft / transform.scale,
        pzright / transform.scale,
        pztop / transform.scale,
        pzbottom / transform.scale
      );
      return viewport;
    },

    /**
     * Copy the selected board elements and connections to the clipboard
     */
    onCopy() {
      if (this.canEdit) {
        this.copy();
      }
    },

    /**
     * Cut the selected board elements and connections to the clipboard
     */
    onCut() {
      if (this.canEdit) {
        this.cut();
      }
    },

    /**
     * Paste the clipboard data to the board
     */
    onPaste() {
      if (!this.getCanPaste || !this.canEdit) {
        return;
      }
      // Get the current viewport dimensions
      const vpRect = this.getViewPort();

      const atPos = this.transformViewportToBoard(this.exactMousePos);
      this.paste({ atPos });
    },

    /**
     * Elevated paste
     */
    onShiftPaste() {
      if (!this.getCanPaste || !this.canEdit) {
        return;
      }
      const vpRect = this.getViewPort();

      const atPos = this.transformViewportToBoard(this.exactMousePos);
      this.shiftPaste({ atPos });
    },

    onEscEvent() {
      window.getSelection().removeAllRanges();
      this.clearSelections();
    },

    /**
     * Event handler for hammer.js touch events. We are handling only 'press' event for pointer type 'touch'.
     * Pointer types are: 'touch', 'mouse', 'pen' or 'kinect'.
     * Type holds the name of the event: e.g. 'panstart', 'press' e.t.c.
     * SrcEvent	holds the source event: TouchEvent, MouseEvent or PointerEvent
     * @param {Object}  event  The hammer.js event
     */
    onTouch(event) {
      // console.log('Board TOUCH EVENT:', event);
      const { type, pointerType } = event;
      if (type === 'press' && pointerType === 'touch') {
        const { target, srcEvent } = event;
        const { clientX, clientY } = srcEvent;
        this.createContextMenu(target, clientX, clientY);
      }
    },

    /**
     * Event handler for context menu event
     * @param {MouseEvent} event
     */
    onContextMenu(event) {
      console.log('Board RIGHT CLICK (context menu) event:', event);
      if (this.canEdit || this.canComment) {
        if (this.getCursorMode === PANZOOM.CURSOR_MODE.GRAB) {
          this.setCursorMode({
            value: PANZOOM.CURSOR_MODE.SELECT,
            apply: true,
          });
        }
        const { clientX, clientY, currentTarget } = event;
        this.createContextMenu(currentTarget, clientX, clientY);
      }
    },

    /**
     * Create the context menu for board
     * @param {Element} element The panzoom element
     * @param {Number}  x       The client x
     * @param {Number}  y       The client y
     */
    async createContextMenu(element, x, y) {
      // Calculate the rectangle of the target element
      const rect = element.getBoundingClientRect();
      const { scale } = this.exactTransform;
      // Find the board position of given client x, y
      const boardPos = {
        x: (x - rect.left) / scale,
        y: (y - rect.top) / scale,
      };

      if (this.canEdit) {
        this.createItem({
          name: 'Create Element',
          icon: null,
          commands: {
            doAction: () => {
              this.createElement({
                boardId: this.getOpen,
                position: boardPos,
                props: null,
                apply: true,
              });
              this.deleteMenu();
            },
            isActive: () => {
              return true;
            },
            isEnabled: () => {
              return true;
            },
          },
          submenu: null,
        });
      }
      if (this.canEdit) {
        this.createItem({
          name: 'Create Note',
          icon: null,
          commands: {
            doAction: () => {
              this.createNote({
                boardId: this.getOpen,
                position: boardPos,
                props: null,
                apply: true,
              });
              this.deleteMenu();
            },
            isActive: () => {
              return true;
            },
            isEnabled: () => {
              return true;
            },
          },
          submenu: null,
        });
      }
      if (this.canEdit) {
        this.createItem({
          name: 'Create Jumper',
          icon: null,
          commands: {
            doAction: () => {
              this.createJumper({
                boardId: this.getOpen,
                position: boardPos,
                props: null,
                apply: true,
              });
              this.deleteMenu();
            },
            isActive: () => {
              return true;
            },
            isEnabled: () => {
              return true;
            },
          },
          submenu: null,
        });
      }
      if (this.canEdit) {
        const clipboard = this.getClipboard;
        this.createItem({
          name: 'Paste as Jumper',
          icon: null,
          commands: {
            doAction: () => {
              this.createJumper({
                boardId: this.getOpen,
                position: boardPos,
                props: { elementId: clipboard.data.elements[0].id },
                apply: true,
              });
              this.deleteMenu();
            },
            isActive: () => {
              return (
                clipboard.data.elements.length === 1 &&
                clipboard.operation === OPERATION.COPY &&
                clipboard.type === TYPE.ELEMENTS
              );
            },
            isEnabled: () => {
              return (
                clipboard.data.elements.length === 1 &&
                clipboard.operation === OPERATION.COPY &&
                clipboard.type === TYPE.ELEMENTS
              );
            },
          },
          submenu: null,
        });
      }
      if (this.canEdit) {
        this.createItem({
          name: 'Create Branch',
          icon: null,
          commands: {
            doAction: () => {
              this.createBranch({
                boardId: this.getOpen,
                position: boardPos,
                props: null,
                apply: true,
              });
              this.deleteMenu();
            },
            isActive: () => {
              return true;
            },
            isEnabled: () => {
              return true;
            },
          },
          submenu: null,
        });
      }
      if (this.canComment) {
        this.createItem({
          name: 'Create Comment',
          icon: null,
          commands: {
            doAction: () => {
              this.createComment({
                props: {
                  userId: this.getId,
                  position: {
                    id: this.getOpen,
                    type: 'boards',
                    x: boardPos.x,
                    y: boardPos.y,
                  },
                  children: [],
                  resolved: false,
                },
                apply: true,
              });
              this.deleteMenu();
            },
            isActive: () => {
              return true;
            },
            isEnabled: () => {
              return true;
            },
          },
          submenu: null,
        });
      }
      if (this.canEdit || this.canComment) {
        this.createItem({
          name: 'Properties...',
          icon: null,
          commands: {
            doAction: () => {
              this.openBoardProperties();
              this.deleteMenu();
            },
            isActive: () => {
              return true;
            },
            isEnabled: () => {
              return true;
            },
          },
        });
      }

      this.setCalcPosition({
        calcPosition: () => {
          return { left: x, top: y };
        },
      });
    },

    /**
     * Open the board properties modal
     */
    openBoardProperties() {
      this.setBoardPropertiesModal({ value: this.getOpen });
    },
  },
};
</script>

<style scoped lang="scss">
.main {
  flex: 1;
  overflow: hidden;
  user-select: none;
  position: relative;
  touch-action: none;
}

.panzoom-parent {
  width: 100%;
  height: 100%;
}

.panzoom {
  touch-action: none;
  user-select: none;
  will-change: contents; //added for chrome bug on transitions (will-change:transform creates blurry text on chrome)
  /* box-shadow: 0px 0px 0px 5px blue inset; */
  width: 20000px;
  height: 20000px;
  background-size: 20px 20px;
  // background-position: 10px 10px;
  background-color: #1e2121;
  background-repeat: repeat;
  position: relative;
  // -webkit-user-drag: element !important;

  &.cursor-grab,
  &.cursor-grab * {
    cursor: grab !important;
  }

  &.showgrid {
    // background-image: url(../../../sass/images/grid-new.svg);
    // background-image: radial-gradient(#373b3c 10%, transparent 0);
    // image-rendering: pixelated;
    // background-size: 30px 30px;
    // background-image: linear-gradient(to right, grey 1px, transparent 1px),
    // linear-gradient(to bottom, grey 1px, transparent 1px);
    // background: url(../../../sass/images/grid.png);
    // image-rendering: pixelated;

    &::after {
      content: '';
      background-image: url(../../../sass/images/grid.png);
      opacity: 0.5;
      background-position: -10px -10px;
      top: 0;
      left: 0;
      bottom: 0;
      right: 0;
      position: absolute;
      z-index: -2;
      background-size: 20px 20px;
      background-repeat: repeat;
    }
  }
}

#rubberband {
  position: absolute;
  z-index: 100;
  border: 1px solid #777;
  background: rgba(255, 255, 255, 0.1);
}
</style>

<style lang="scss">
/* ELEMENT STYLES */
@import './BoardElementStyles.scss';

.jtk-overlay.label {
  // border: 1px solid #424b4c;

  color: #c6c6c6;
  // padding: 8px 10px 8px 10px;
  z-index: 5;
  .labelEditor {
    background: #2b3132;
    border-radius: 4px;
    overflow: hidden;
    .ProseMirror {
      // padding: 10px;
      pre {
        background: #0d0e0e;
        border-radius: 0;
      }
      p {
        padding: 6px 10px;
      }
      pre,
      p {
        margin: 0;
        min-height: 33px;
        // &:first-child {
        //   margin: 0;
        // }
      }
    }

    .validation {
      z-index: 1;
      bottom: -10px;
      right: -10px;
    }

    .mention {
      display: inline-block;
      cursor: pointer;
      color: #db841e;

      &:hover {
        text-decoration: underline;
      }
    }
  }
}

.panzoom {
  &.pan-mode,
  &.cursor-grab {
    .jtk-overlay.label .remove {
      display: none;
    }
  }
  .jtk-overlay.label:hover .remove,
  .jtk-overlay.label .remove.visible {
    opacity: 1 !important;
  }
}

.jtk-overlay.label .text {
  font-family: 'Open Sans', sans-serif;
  display: block;
  min-width: 10px;
  background: transparent;
  border: none;
  color: #c6c6c6;
  width: auto;
  display: inline-block;

  &:empty:before {
    content: attr(placeholder);
    display: block;
    color: #818c8e;
  }
}

.jtk-overlay.label .text:focus {
  color: #fff;
}

.jtk-overlay.label .remove {
  opacity: 0;
  position: absolute;
  top: -10px;
  right: -10px;
  color: #fff;
  font-weight: 700;
  cursor: pointer;
  background: #4f5e61;
  border-radius: 999px;
  width: 20px;
  height: 20px;
  display: flex;
  z-index: 1;
  justify-content: center;
  align-items: center;

  &:hover {
    background: #db841e;
    svg path {
      fill: #fff;
    }
  }

  svg {
    width: 8px;
    height: 8px;
  }
}

.jtk-connector {
  // cursor: pointer;
  z-index: 1;
}

.jtk-connector.jtk-dragging {
  z-index: 4;
}

.jtk-endpoint {
  z-index: 4;
}

.jtk-endpoint.target {
  z-index: 91;
}

//TRANSITIONS
.overlay_container.fade-enter-active,
.overlay_container.fade-leave-active {
  transition: opacity 0.2s ease;

  .modal {
    transition: transform 0.2s;
    transform-origin: center 100px;
    transform: scale(1);
  }
}

.overlay_container.fade-enter-from,
.overlay_container.fade-leave-to {
  opacity: 0;

  .modal {
    transform-origin: center 100px;
    transition: transform 0.2s;
    transform: scale(0.75);
  }
}
</style>

<style lang="scss">
//CONNECTION-HANDLERS FOR ELEMENTS, JUMPERS,BRANCHES

.plank .connection-handler,
.jumper .connection-handler,
.branch .connection-handler {
  position: absolute;
  opacity: 0;
  &.jtk-drag-hover,
  &.active-handler {
    transition: opacity 0.1s ease-in;
    cursor: crosshair;
    opacity: 1;
  }

  &.up {
    left: 10px !important;
    right: 10px !important;
    top: -20px;
    height: 20px;
    &:after {
      content: '';
      background: #db841e !important;
      position: absolute;
      left: 0;
      right: 0;
      bottom: 0;
      height: 50%;
      border-radius: 100px 100px 0 0;
    }
  }
  &.right {
    top: 10px !important;
    bottom: 10px !important;
    right: -20px;
    height: auto !important;
    width: 20px;
    &:after {
      content: '';
      background: #db841e !important;
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: 50%;
      border-radius: 0 100px 100px 0;
    }
  }
  &.down {
    left: 10px !important;
    right: 10px !important;
    bottom: -20px;
    height: 20px;
    &:after {
      content: '';
      background: #db841e !important;
      position: absolute;
      left: 0;
      right: 0;
      top: 0;
      height: 50%;
      border-radius: 0 0 100px 100px;
    }
  }
  &.left {
    top: 10px !important;
    bottom: 10px !important;
    left: -20px;
    height: auto !important;
    background: none !important;
    width: 20px;
    &:after {
      content: '';
      background: #db841e !important;
      position: absolute;
      right: 0;
      top: 0;
      bottom: 0;
      width: 50%;
      border-radius: 100px 0 0 100px;
    }
  }
}

#panzoom.pan-mode,
#panzoom.cursor-grab {
  .plank .connection-handler,
  .jumper .connection-handler,
  .branch .connection-handler {
    &.jtk-drag-hover,
    &.active-handler {
      cursor: grab;
    }
  }
}

//Make sure handlers do not appear while dragging items
body:not(.katavorio-drag-no-select)
  #panzoom:not(.pan-mode):not(.cursor-grab)
  .plank
  .connection-handler {
  &:hover {
    transition: opacity 0.1s ease-in;
    cursor: crosshair;
    opacity: 1;
  }
}

//ELEMENT AND NOTE SELECTION AND RESIZABLE STYLES

.plank.user-selected::after,
.note.user-selected::after,
.branch.user-selected::after {
  content: '';
  position: absolute;
  left: -1px;
  top: -1px;
  right: -1px;
  bottom: -1px;
  border: 2px solid var(--user-selected-color) !important;
  border-radius: 9px;
  pointer-events: none;
  z-index: 1;
}

.jumper.user-selected .handler::after {
  content: '';
  position: absolute;
  left: 0; //-1px;
  top: 0; //-1px;
  right: 0; //-1px;
  bottom: 0; //-1px;
  border: 2px solid var(--user-selected-color) !important;
  border-right: none !important;
  border-radius: 9px 0 0 9px;
  pointer-events: none;
}
.jumper.user-selected {
  .svg-icon-container {
    filter: drop-shadow(2px 0px 0px var(--user-selected-color));
  }
}

.showSelections {
  .plank.jtk-drag-selected::after,
  .note.jtk-drag-selected::after,
  .branch.jtk-drag-selected::after,
  .plank.virtual-selected::after,
  .note.virtual-selected::after,
  .branch.virtual-selected::after {
    content: '';
    position: absolute;
    left: -1px;
    top: -1px;
    right: -1px;
    bottom: -1px;
    border: 2px solid #db841e !important;
    //border: calc(var(--transform-default-scale) * 2px) solid #db841e !important;
    border-radius: 9px;
    pointer-events: none;
    z-index: 1;
  }

  .note.jtk-drag-selected::after,
  .note.virtual-selected::after {
    border-radius: 5px;
  }

  .note.user-selected::after {
    border-radius: 6px;
  }

  .jumper.jtk-drag-selected .handler::after,
  .jumper.virtual-selected .handler::after {
    content: '';
    position: absolute;
    left: 0; //-1px;
    top: 0; //-1px;
    right: 0; //-1px;
    bottom: 0; //-1px;
    border: 2px solid #db841e !important;
    //border: calc(var(--transform-default-scale) * 2px) solid #db841e !important;
    border-radius: 9px;
    pointer-events: none;
    // z-index: 1;
  }

  .plank .ui-resizable-handle,
  .note .ui-resizable-handle {
    display: block !important;
  }
}

.plank .ui-resizable-handle,
.note .ui-resizable-handle {
  position: absolute;
  font-size: 0.1px;
  display: none !important;
  -ms-touch-action: none;
  touch-action: none;
  z-index: 93 !important;
  height: 14px;
  width: 14px;
  background: #6d7171;
  border-radius: 999px;
  box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.5);
  //transform: scale(var(--transform-default-scale));
  // box-shadow: inset 0 0 0 2px #db841e;

  &:hover {
    background: #db841e;
  }
}
.plank.ui-resizable-disabled .ui-resizable-handle,
.note.ui-resizable-disabled .ui-resizable-handle,
.plank.ui-resizable-autohide .ui-resizable-handle,
.plank.ui-resizable-autohide .ui-resizable-handle {
  display: none !important;
}
.plank[data-virtual-selected='true'] .ui-resizable-handle,
.note[data-virtual-selected='true'] .ui-resizable-handle {
  display: block !important;
}
.plank .ui-resizable-n,
.note .ui-resizable-n,
.plank .ui-resizable-w,
.note .ui-resizable-w,
.plank .ui-resizable-s,
.note .ui-resizable-s,
.plank .ui-resizable-e,
.note .ui-resizable-e {
  opacity: 0;
  border-radius: 0;
  box-shadow: none;
  z-index: 92 !important;
}
.plank .ui-resizable-n,
.note .ui-resizable-n {
  cursor: n-resize;
  top: 0;
  height: 10px;
  transform-origin: bottom left;
}
.plank .ui-resizable-s,
.note .ui-resizable-s {
  cursor: s-resize;
  bottom: 0;
  height: 10px;
  transform-origin: top left;
}
.plank .ui-resizable-e,
.note .ui-resizable-e {
  cursor: e-resize;
  right: 0;
  width: 10px;
  transform-origin: top left;
}
.plank .ui-resizable-w,
.note .ui-resizable-w {
  cursor: w-resize;
  left: 0;
  width: 10px;
  transform-origin: top right;
}

.plank .ui-resizable-n,
.note .ui-resizable-n,
.plank .ui-resizable-s,
.note .ui-resizable-s {
  width: 100%;
}

.plank .ui-resizable-e,
.note .ui-resizable-e,
.plank .ui-resizable-w,
.note .ui-resizable-w {
  height: 100%;
}

.plank .ui-resizable-se,
.note .ui-resizable-se {
  cursor: se-resize;
  right: -8px;
  bottom: -8px;
}
.plank .ui-resizable-sw,
.note .ui-resizable-sw {
  cursor: sw-resize;
  left: -8px;
  bottom: -8px;
}
.plank .ui-resizable-nw,
.note .ui-resizable-nw {
  cursor: nw-resize;
  left: -8px;
  top: -8px;
}
.plank .ui-resizable-ne,
.note .ui-resizable-ne {
  cursor: ne-resize;
  right: -8px;
  top: -8px;
}

//BRANCH CONDITION ENDPOINT STYLES
.condition-source {
  &.jtk-endpoint-connected.jtk-dragging {
    display: none;
  }
  &.jtk-drag-hover svg circle {
    fill: #db841e;
    stroke: #db841e;
  }
}

//GUEST STYLE FIXES
.panzoom.pan-mode,
.panzoom.cursor-grab {
  &:not(.guest) .comment {
    cursor: grab !important;
  }

  &.guest {
    // .panzoom.isPanning {
    cursor: grab !important;

    .jtk-hover,
    .jtk-connector,
    .source-hover,
    .plank,
    .plank .cover *,
    .jumper .container,
    .jumper .board,
    .editor,
    .content,
    .compgroup,
    .title,
    .label input,
    .source-hover {
      cursor: grab !important;
    }

    .comment {
      cursor: default !important;
    }

    // .jtk-endpoint.source circle {
    //   fill: rgb(243, 243, 243) !important;
    // }
  }
}

body.selecting,
body.selecting * {
  cursor: default !important;
}

// When dragging do not interact with endpoints-connections (to have correct pointer events on elements)
.dragging .panzoom .jtk-endpoint,
.dragging .panzoom .jtk-endpoint svg,
.dragging .panzoom .jtk-endpoint svg circle,
.dragging .panzoom .jtk-endpoint svg rect,
.dragging .panzoom .jtk-connector,
.dragging .panzoom .jtk-connector path {
  pointer-events: none;
}

#crosshair {
  position: relative;
  z-index: 1000;
  left: calc(50% - 35px);
  top: calc(50% - 35px);
}

#crosshair::after,
#crosshair::before {
  content: '';
  background: black;
  border-radius: 2px;
  position: absolute;
}

#crosshair::after {
  left: 30px;
  width: 10px;
  height: 20px;
  box-shadow: 0 50px black;
}

#crosshair::before {
  top: 30px;
  width: 20px;
  height: 10px;
  box-shadow: 50px 0 black;
}
</style>
@/lib/jspSingleton.js
